// ============================================================================
//  EM²C Mars Colony — procedural building kit for Three.js
//  53 Mars-colony buildings in one visual language: armoured white/grey
//  panel blocks on dark bases, orange trim, glowing windows and airlocks —
//  built hex by hex so every cell is filled and every pipe is anchored.
//
//  Usage:
//    import { createBuilding, BUILDINGS, updateBuilding } from './buildings.js';
//    const cc = createBuilding('1A');            // THREE.Group
//    cc.position.copy(hexCenter);                // anchor hex centre
//    cc.scale.setScalar(yourHexRadius);          // models use hex radius = 1
//    scene.add(cc);
//    // per frame:  updateBuilding(cc, clock.elapsedTime)
//
//  Grid convention: FLAT-TOP hexes, axial (q, r) coords, radius 1.
//    world.x = 1.5 * q            world.z = √3 * (r + q / 2)
//  Group origin = the footprint's ANCHOR cell [0,0] centre, ground level.
//  Rotating a footprint by `steps` × 60°: rotateFootprint(cells, steps)
//  and group.rotation.y = footprintRotationY(steps).
// ============================================================================
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

export const HEX_RADIUS = 1;
export const PLINTH_H = 0.1;
const SQ3 = Math.sqrt(3), PI = Math.PI, TAU = PI * 2;
const Y = PLINTH_H;

// ---------------------------------------------------------------- hex math
export function axialToWorld(q, r, size = HEX_RADIUS) {
  return { x: size * 1.5 * q, z: size * SQ3 * (r + q / 2) };
}
export const HEX_DIRS = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]];
/** Canonical connected footprints (axial offsets from the anchor cell). */
export const FOOTPRINT_SHAPES = {
  1: [[0, 0]],
  2: [[0, 0], [0, 1]],                                   // straight pair
  3: [[0, 0], [0, 1], [1, 0]],                           // tight triangle
  5: [[0, 0], [1, 0], [1, -1], [-1, 0], [-1, 1]],        // wide bow-tie
  7: [[0, 0], [1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]], // flower
};
export function rotateFootprint(cells, steps = 0) {
  let out = cells.map(c => [c[0], c[1]]);
  const n = ((steps % 6) + 6) % 6;
  for (let s = 0; s < n; s++) out = out.map(([q, r]) => [-r, q + r]);
  return out;
}
export const footprintRotationY = steps => -steps * PI / 3;

// ---------------------------------------------------------------- materials
let M = null;
export function getMaterials() {
  if (M) return M;
  const S = (c, r = .5, m = .1, x = {}) => new THREE.MeshStandardMaterial({ color: c, roughness: r, metalness: m, ...x });
  const E = (c, i = 2, x = {}) => new THREE.MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: i, roughness: .4, ...x });
  M = {
    white: S(0xdadde0, .55, .05), panel: S(0xb6bcc3, .6, .05), grey: S(0x868d95, .55, .2), cream: S(0xd4ccbc, .7, .05), orange: S(0xf06a1a, .45, .1),
    dark: S(0x2c3036, .55, .45), darker: S(0x17191c, .6, .4), slab: S(0x54463f, .8, .25),
    steel: S(0x9aa1a9, .35, .7), glass: S(0x0e1a22, .08, .6), yellow: S(0xf2c21b, .5, .1),
    glassClear: S(0xb8e2f5, .05, .1, { transparent: true, opacity: .3, depthWrite: false, side: THREE.DoubleSide }),
    dish: S(0xdadde0, .5, .1, { side: THREE.DoubleSide }), inner: S(0x17191c, .7, .3, { side: THREE.DoubleSide }), dust: S(0x7a4430, .95, 0), radiator: S(0xdfe3e6, .4, .3),
    amber: E(0xffa53a, 2.2), cyan: E(0x36c9ff, 1.6), red: E(0xff3020, 2.5), green: E(0x3cff7a, 1.5),
    magenta: E(0xd04dff, 1.8), screen: E(0x2aa8e0, 1.1), teal: E(0x2fd6c0, 1.4), lava: E(0xff7a1a, 2.6),
    flame: E(0xffc04a, 3, { transparent: true, opacity: .9 }), blue: S(0x2a6fb8, .4, .2),
    plant: S(0x3f9a3a, .8, 0), plant2: S(0x76c043, .8, 0), soil: S(0x3a2a20, .95, 0),
    regolith: S(0x9a5233, .95, 0), rock: S(0x5e5650, .9, 0), ore: S(0x3b3e44, .6, .5),
    concrete: S(0x8d8a86, .9, .05), crate: S(0x8a6a44, .85, 0), rubber: S(0x1e1f22, .9, 0),
    water: S(0x2a7fc0, .1, .3, { transparent: true, opacity: .85 }), solar: S(0x1a2c55, .25, .6),
    frost: S(0xdfeaf4, .3, .1), smoke: S(0xc9c2bb, 1, 0, { transparent: true, opacity: .4, depthWrite: false }),
    steam: S(0xf4e9e2, 1, 0, { transparent: true, opacity: .22, depthWrite: false }),
  };
  return M;
}

// ---------------------------------------------------------------- primitives
const sh = m => { m.castShadow = true; m.receiveShadow = true; return m; };
function add(p, geo, mat, x = 0, y = 0, z = 0, ry = 0) {
  const m = sh(new THREE.Mesh(geo, mat)); m.position.set(x, y, z); m.rotation.y = ry; p.add(m); return m;
}
function grp(p, x = 0, y = 0, z = 0, ry = 0) {
  const g = new THREE.Group(); g.position.set(x, y, z); g.rotation.y = ry; p.add(g); return g;
}
/** box with BOTTOM at y */
const box = (p, w, h, d, mat, x = 0, y = 0, z = 0, ry = 0) => add(p, new THREE.BoxGeometry(w, h, d), mat, x, y + h / 2, z, ry);
/** box CENTRED at y */
const boxc = (p, w, h, d, mat, x = 0, y = 0, z = 0, ry = 0) => add(p, new THREE.BoxGeometry(w, h, d), mat, x, y, z, ry);
/** cylinder with BOTTOM at y */
const cyl = (p, rt, rb, h, mat, x = 0, y = 0, z = 0, seg = 16, ts = 0) =>
  add(p, new THREE.CylinderGeometry(rt, rb, h, seg, 1, false, ts), mat, x, y + h / 2, z);
/** n-sided prism with a flat face toward +z */
const prism = (p, n, rb, rt, h, mat, x = 0, y = 0, z = 0) => cyl(p, rt, rb, h, mat, x, y, z, n, -PI / n);
const sphere = (p, r, mat, x = 0, y = 0, z = 0, seg = 16) => add(p, new THREE.SphereGeometry(r, seg, Math.max(6, seg >> 1)), mat, x, y, z);
function torus(p, r, t, mat, x = 0, y = 0, z = 0, flat = true, arc = TAU, seg = 32) {
  const m = add(p, new THREE.TorusGeometry(r, t, 6, seg, arc), mat, x, y, z); if (flat) m.rotation.x = PI / 2; return m;
}
const V = (x, y, z) => new THREE.Vector3(x, y, z);
const UP = V(0, 1, 0);
let PIPES = [];
function pipe(p, a, b, r, mat, seg = 8) {
  const A = Array.isArray(a) ? V(...a) : a, B = Array.isArray(b) ? V(...b) : b;
  const d = V(B.x - A.x, B.y - A.y, B.z - A.z), L = d.length();
  const m = add(p, new THREE.CylinderGeometry(r, r, L, seg), mat);
  m.position.set((A.x + B.x) / 2, (A.y + B.y) / 2, (A.z + B.z) / 2);
  m.quaternion.setFromUnitVectors(UP, d.normalize()); m.userData.pipe = true; PIPES.push({ p, a: A.clone(), b: B.clone(), r }); return m;
}
function pipeRun(p, pts, r, mat = M.steel, joints = true) {
  for (let i = 0; i < pts.length - 1; i++) pipe(p, pts[i], pts[i + 1], r, mat);
  if (joints) for (let i = 1; i < pts.length - 1; i++) sphere(p, r * 1.35, mat, ...pts[i], 8);
}
function markDyn(o) { o.userData.dynamic = true; return o; }

// ------------------------------------------------- faces / parts
function fspec(n, rb, rt, h, y0) { return { n, rb, rt, h, y0, tilt: Math.atan2((rb - rt) * Math.cos(PI / n), h) }; }
/** group sitting on face i of prism spec f at relative height v (0..1). local x across, y up the face, z out. */
function faceGroup(p, f, i, v, out = 0) {
  const R = f.rb + (f.rt - f.rb) * v, ap = R * Math.cos(PI / f.n), a = TAU * i / f.n;
  const g = new THREE.Group(); g.rotation.order = 'YXZ'; g.rotation.set(-f.tilt, a, 0);
  g.position.set(Math.sin(a) * (ap + out) + (f.ox || 0), f.y0 + v * f.h, Math.cos(a) * (ap + out) + (f.oz || 0));
  g.userData.fw = 2 * R * Math.sin(PI / f.n); p.add(g); return g;
}
function win(q, w, h, x = 0, y = 0, glassMat = M.glass) {
  boxc(q, w + .028, h + .028, .012, M.darker, x, y, .004);
  boxc(q, w, h, .016, glassMat, x, y, .008);
}
function logo(q, s = 1, x = 0, y = 0) { // original EM²C chevron mark
  const a = boxc(q, .022 * s, .13 * s, .01, M.orange, x - .033 * s, y + .01 * s, .01); a.rotation.z = -.47;
  const b = boxc(q, .022 * s, .13 * s, .01, M.orange, x + .033 * s, y + .01 * s, .01); b.rotation.z = .47;
  boxc(q, .13 * s, .02 * s, .01, M.orange, x, y - .07 * s, .01);
}
function plus(p, s, mat, x, y, z, flat = true) {
  const g = grp(p, x, y, z); if (flat) g.rotation.x = -PI / 2;
  boxc(g, s, s * .32, .012, mat); boxc(g, s * .32, s, .012, mat); return g;
}

function beacon(p, c, x, y, z, mat = M.red, r = .022, period = 1.4) {
  const b = markDyn(sphere(p, r, mat, x, y, z, 10));
  c.dyn.push(t => { b.visible = ((t / period) % 1) < .55; });
  return b;
}
function mast(p, c, x, y0, z, h, r = .012, tip = M.red) {
  cyl(p, r * .6, r, h, M.dark, x, y0, z, 6);
  if (tip === M.red) beacon(p, c, x, y0 + h, z, tip, r * 1.6); else sphere(p, r * 1.6, tip, x, y0 + h, z, 8);
}
function tank(p, r, h, x, z, y0 = Y, o = {}) {
  const q = grp(p, x, y0, z);
  cyl(q, r, r, h, o.mat || M.white, 0, 0, 0, 20);
  const d = add(q, new THREE.SphereGeometry(r, 20, 8, 0, TAU, 0, PI / 2), o.mat || M.white, 0, h, 0); d.scale.y = o.domeH ?? .4;
  cyl(q, r * 1.05, r * 1.06, .035, M.dark, 0, 0, 0, 20);
  for (const [v, m] of (o.bands || [[.72, M.orange]])) cyl(q, r + .005, r + .005, .03, m, 0, h * v, 0, 20);
  if (o.ladder !== false) {
    const a = o.ladderAng ?? PI / 4;
    box(q, .045, h, .012, M.steel, Math.sin(a) * (r + .01), 0, Math.cos(a) * (r + .01), a);
  }
  if (o.rail) torus(q, r * .92, .006, M.steel, 0, h + r * (o.domeH ?? .4) * .3 + .04, 0);
  return q;
}
function hTank(p, r, len, x, y, z, ry = 0, o = {}) {
  const q = grp(p, x, y, z, ry);
  const b = add(q, new THREE.CylinderGeometry(r, r, len, 16), o.mat || M.white, 0, r + .03, 0); b.rotation.x = PI / 2;
  for (const s of [-1, 1]) {
    const e = add(q, new THREE.SphereGeometry(r, 16, 8, 0, TAU, 0, PI / 2), o.mat || M.white, 0, r + .03, s * len / 2);
    e.rotation.x = s * PI / 2; e.scale.y = .5;
    box(q, r * 1.6, r * .7, .05, M.dark, 0, 0, s * len * .3);
    const band = add(q, new THREE.CylinderGeometry(r + .005, r + .005, .03, 16), o.band || M.orange, 0, r + .03, s * len * .2); band.rotation.x = PI / 2;
  }
  return q;
}
function sphereTank(p, r, x, z, y0 = Y, legH = .2, band = M.cyan) {
  const q = grp(p, x, y0, z), cy = legH + r;
  for (let k = 0; k < 4; k++) {
    const a = PI / 4 + k * PI / 2;
    pipe(q, [Math.sin(a) * r * .85, 0, Math.cos(a) * r * .85], [Math.sin(a) * r * .72, cy, Math.cos(a) * r * .72], .018, M.dark);
    box(q, .06, .02, .06, M.dark, Math.sin(a) * r * .85, 0, Math.cos(a) * r * .85);
  }
  sphere(q, r, M.white, 0, cy, 0, 24);
  torus(q, r + .003, .014, band, 0, cy, 0);
  torus(q, r * .25, .015, M.dark, 0, cy + r * .97, 0);
  return q;
}
function dome(p, r, mat, x, y, z, ribs = 6, ribMat = M.steel, flatten = 1) {
  const q = grp(p, x, y, z);
  const d = add(q, new THREE.SphereGeometry(r, 24, 10, 0, TAU, 0, PI / 2), mat); d.scale.y = flatten;
  for (let k = 0; k < ribs; k++) {
    const rb = add(q, new THREE.TorusGeometry(r, .009, 4, 20, PI), ribMat); rb.rotation.y = k * PI / ribs; rb.scale.y = flatten;
  }
  torus(q, r, .016, ribMat, 0, 0, 0);
  return q;
}
function dish(p, c, x, y, z, r, o = {}) {
  const base = grp(p, x, y, z), ph = o.post ?? r * .55;
  cyl(base, r * .14, r * .22, ph, M.dark, 0, 0, 0, 10);
  const yaw = grp(base, 0, ph, 0, o.yaw ?? 0);
  if (o.spin) { markDyn(yaw); c.dyn.push(t => { yaw.rotation.y = (o.yaw ?? 0) + Math.sin(t * o.spin) * .9; }); }
  box(yaw, r * .45, r * .14, r * .3, M.white, 0, 0, 0);
  box(yaw, r * .47, .02, r * .32, M.orange, 0, r * .07, 0);
  const tilt = grp(yaw, 0, r * .2, 0); tilt.rotation.x = o.tilt ?? .7;
  const cup = add(tilt, new THREE.SphereGeometry(r, 24, 6, 0, TAU, 0, .78), M.dish, 0, r, 0); cup.rotation.x = PI;
  const rim = r * Math.sin(.78), rimY = r - r * Math.cos(.78);
  torus(tilt, rim, .012, M.dark, 0, rimY, 0);
  for (let k = 0; k < 3; k++) {
    const a = k * TAU / 3;
    pipe(tilt, [Math.sin(a) * rim * .95, rimY, Math.cos(a) * rim * .95], [0, r * .62, 0], .008, M.dark, 5);
  }
  cyl(tilt, .03 * r / .5, .045 * r / .5, .07 * r / .5, M.orange, 0, r * .58, 0, 8);
  return base;
}
function lattice(p, x, z, y0, h, rb, rt, n = 4, levels = 5, mat = M.dark, r = .012) {
  const q = grp(p, x, y0, z);
  const pt = (k, lv) => { const t = lv / levels, rr = rb + (rt - rb) * t, a = PI / 4 + k * TAU / n; return [Math.sin(a) * rr, h * t, Math.cos(a) * rr]; };
  for (let k = 0; k < n; k++) {
    pipe(q, pt(k, 0), pt(k, levels), r, mat, 6);
    for (let lv = 0; lv < levels; lv++) {
      pipe(q, pt(k, lv), pt((k + 1) % n, lv + 1), r * .55, mat, 4);
      pipe(q, pt(k, lv + 1), pt((k + 1) % n, lv + 1), r * .55, mat, 4);
    }
  }
  return q;
}
function pile(p, x, z, y0, r, h, mat = M.regolith, rocks = 5) {
  const q = grp(p, x, y0, z);
  add(q, new THREE.ConeGeometry(r, h, 9), mat, 0, h / 2, 0);
  for (let i = 0; i < rocks; i++) {
    const a = i * 2.4, rr = r * (.55 + .4 * ((i * 37) % 10) / 10), s = r * .14;
    const m = add(q, new THREE.IcosahedronGeometry(s, 0), mat === M.rock ? M.ore : M.rock, Math.sin(a) * rr, s * .6 + h * .25 * (1 - rr / r), Math.cos(a) * rr);
    m.rotation.set(i, i * 2, 0);
  }
  return q;
}
function solarPanel(p, x, z, y0, w, d, tilt = .5, ry = 0, h = .12) {
  const q = grp(p, x, y0, z, ry);
  for (const sx of [-1, 1]) cyl(q, .012, .014, h, M.dark, sx * w * .3, 0, 0, 6);
  const t = grp(q, 0, h, 0); t.rotation.x = tilt;
  boxc(t, w + .02, .014, d + .02, M.steel, 0, -.006, 0);
  boxc(t, w, .012, d, M.solar, 0, .002, 0);
  for (let i = 1; i < 4; i++) boxc(t, .006, .014, d, M.steel, -w / 2 + i * w / 4, .002, 0);
  boxc(t, w, .014, .006, M.steel, 0, .002, 0);
  return q;
}
function plants(p, x, z, y0, w, d, nx, nz, s = .05) {
  const q = grp(p, x, y0, z);
  box(q, w, .03, d, M.soil, 0, 0, 0);
  for (let i = 0; i < nx; i++) for (let j = 0; j < nz; j++) {
    const m = (i + j) % 3 === 0 ? M.plant2 : M.plant;
    sphere(q, s * (.8 + .4 * (((i * 7 + j * 3) % 5) / 5)), m, -w / 2 + (i + .5) * w / nx, .03 + s * .6, -d / 2 + (j + .5) * d / nz, 6);
  }
  return q;
}
function conveyor(p, a, b, w = .08, ore = true) {
  const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2], L = Math.hypot(dx, dy, dz);
  const q = grp(p, ...a); q.rotation.order = 'YXZ'; q.rotation.y = Math.atan2(dx, dz); q.rotation.x = -Math.atan2(dy, Math.hypot(dx, dz));
  boxc(q, w, .02, L, M.darker, 0, 0, L / 2);
  for (const sx of [-1, 1]) boxc(q, .012, .04, L, M.steel, sx * w / 2, .008, L / 2);
  if (ore) for (let i = 1; i < L / .07; i++) boxc(q, w * .5, .02, .035, M.rock, ((i * 13) % 5 - 2) * w * .06, .02, i * .07);
  for (const t of [.2, .55, .9]) {
    const py = a[1] + dy * t;
    if (py - Y > .04) box(p, .022, py - Y, .022, M.dark, a[0] + dx * t, Y, a[2] + dz * t);
  }
  return q;
}
function railing(p, r, x, y, z, n = 12) {
  torus(p, r, .006, M.steel, x, y + .05, z);
  for (let k = 0; k < n; k++) { const a = k * TAU / n; cyl(p, .004, .004, .05, M.steel, x + Math.sin(a) * r, y, z + Math.cos(a) * r, 4); }
}
function fan(p, c, x, y, z, r, speed = 4) {
  cyl(p, r + .015, r + .015, .03, M.dark, x, y, z, 16);
  const f = markDyn(grp(p, x, y + .03, z));
  for (let k = 0; k < 4; k++) box(f, r * 1.8, .006, r * .3, M.steel, 0, 0, 0, k * PI / 4);
  cyl(f, r * .2, r * .2, .015, M.orange, 0, 0, 0, 8);
  c.dyn.push(t => { f.rotation.y = t * speed; });
}

function commandTower(g, c, s = 1) {
  const q = grp(g); q.scale.setScalar(s);
  prism(q, 6, .84, .82, .04, M.dark, 0, Y, 0);
  const f = fspec(6, .76, .58, .38, Y + .04);
  prism(q, 6, f.rb, f.rt, f.h, M.white, 0, f.y0, 0);
  for (let i = 0; i < 6; i++) {
    const lo = faceGroup(q, f, i, .06, .002); boxc(lo, lo.userData.fw * .92, .03, .02, M.dark, 0, 0, 0);
    const mid = faceGroup(q, f, i, .55, .002), fw = mid.userData.fw;
    for (const sx of [-1, 1]) boxc(mid, .008, f.h * .9, .014, M.panel, sx * fw * .48, -.01, 0);
    if (i === 0) {
      logo(faceGroup(q, f, i, .74, .002), .9);
      for (const sx of [-1, 1]) boxc(mid, .035, .1, .014, M.orange, sx * .19, -.06, .004);
      for (const sx of [-1, 1]) boxc(mid, .014, .06, .02, M.darker, sx * .26, -.05, .006);
    } else if (i === 3) {
      for (let k = 0; k < 4; k++) boxc(mid, .26, .012, .016, M.darker, 0, .05 - k * .03, .004);
      boxc(mid, .3, .09, .014, M.orange, 0, -.12, .002).scale.set(1, .2, 1);
    } else {
      win(mid, .22, .085, 0, .02);
      boxc(mid, .035, .1, .014, M.orange, (i < 3 ? -1 : 1) * fw * .36, -.02, .004);
      boxc(mid, .1, .02, .016, M.darker, 0, -.1, .004);
    }
  }
  door(q, 0, f.y0, .63, 0, 1);
  // control room
  const y1 = f.y0 + f.h;
  prism(q, 6, .6, .6, .035, M.darker, 0, y1, 0);
  const cf = fspec(6, .5, .66, .25, y1 + .035);
  prism(q, 6, cf.rb, cf.rt, cf.h, M.glass, 0, cf.y0, 0);
  for (let i = 0; i < 6; i++) {
    const sg = faceGroup(q, cf, i, .5, -.006);
    boxc(sg, .1, .08, .004, M.screen, -.11, .01, 0); boxc(sg, .1, .08, .004, M.screen, .11, .01, 0);
    boxc(sg, sg.userData.fw * .85, .012, .006, M.amber, 0, -.1, .002);
    if (i === 0) sphere(sg, .028, M.lava, 0, .005, -.002, 10);
    const a = -PI / 6 + i * PI / 3;
    pipe(q, [Math.sin(a) * .5, cf.y0, Math.cos(a) * .5], [Math.sin(a) * .66, cf.y0 + cf.h, Math.cos(a) * .66], .016, M.darker, 6);
    box(q, .02, .03, .02, M.amber, Math.sin(a) * .62, y1 - .002, Math.cos(a) * .62);
  }
  // roof
  const y3 = cf.y0 + cf.h, rf = fspec(6, .7, .69, .055, y3);
  prism(q, 6, rf.rb, rf.rt, rf.h, M.white, 0, y3, 0);
  for (let i = 0; i < 6; i++) { const t = faceGroup(q, rf, i, .5, .002); boxc(t, t.userData.fw * .55, .02, .012, M.orange, 0, 0, 0); }
  const r2 = fspec(6, .66, .46, .07, y3 + .055);
  prism(q, 6, r2.rb, r2.rt, r2.h, M.panel, 0, r2.y0, 0);
  for (const i of [1, 2, 4, 5]) { const t = faceGroup(q, r2, i, .5, .003); boxc(t, .13, .035, .012, M.dark, 0, 0, 0); }
  const y4 = r2.y0 + r2.h;
  cyl(q, .25, .28, .02, M.dark, 0, y4, 0, 6, -PI / 6);
  torus(q, .2, .012, M.orange, 0, y4 + .022, 0);
  cyl(q, .15, .17, .03, M.white, 0, y4 + .02, 0, 16);
  cyl(q, .045, .055, .14, M.dark, 0, y4 + .05, 0, 10);
  cyl(q, .057, .057, .015, M.amber, 0, y4 + .1, 0, 10);
  cyl(q, .13, .13, .012, M.white, 0, y4 + .19, 0, 20);
  torus(q, .13, .006, M.steel, 0, y4 + .23, 0);
  cyl(q, .03, .04, .08, M.dark, 0, y4 + .2, 0, 8);
  cyl(q, .014, .022, .26, M.darker, 0, y4 + .28, 0, 6);
  cyl(q, .018, .018, .012, M.amber, 0, y4 + .5, 0, 8);
  beacon(q, c, 0, y4 + .55, 0, M.amber, .016, 1.6);
  mast(q, c, .08, y4 + .2, .02, .2, .009);
  mast(q, c, -.075, y4 + .2, -.03, .14, .008, M.amber);
  return q;
}



function hullShape(w, h, wall = .36, seg = 8) {
  const s = new THREE.Shape(), rx = w / 2, cy = h * wall, ry = h - cy;
  s.moveTo(-rx, 0); s.lineTo(rx, 0); s.lineTo(rx, cy);
  for (let i = 1; i < seg; i++) { const a = i / seg * PI; s.lineTo(Math.cos(a) * rx, cy + Math.sin(a) * ry); }
  s.lineTo(-rx, cy); s.closePath(); return s;
}
function hullGeo(w, h, d, z0 = -d / 2, wall = .36, seg = 8) {
  const g = new THREE.ExtrudeGeometry(hullShape(w, h, wall, seg), { depth: d, bevelEnabled: false }); g.translate(0, 0, z0); return g;
}
/** Airlock vestibule with octagonal pressure hatch; faces local +z. */
function door(p, x, y, z, ry = 0, s = 1) {
  const d = grp(p, x, y, z, ry); d.scale.setScalar(s);
  add(d, hullGeo(.32, .34, .17, -.11, .5, 6), M.panel);
  const fr = add(d, new THREE.CylinderGeometry(.11, .11, .02, 8, 1, false, PI / 8), M.darker, 0, .14, .066); fr.rotation.x = PI / 2; fr.scale.z = 1.15;
  const hp = add(d, new THREE.CylinderGeometry(.085, .085, .02, 8, 1, false, PI / 8), M.steel, 0, .14, .074); hp.rotation.x = PI / 2; hp.scale.z = 1.15;
  boxc(d, .006, .17, .022, M.darker, 0, .14, .078);
  const ring = add(d, new THREE.TorusGeometry(.1, .007, 4, 8), M.amber, 0, .14, .072); ring.rotation.z = PI / 8; ring.scale.y = 1.15;
  boxc(d, .03, .014, .01, M.cyan, .1, .27, .062);
  box(d, .3, .016, .1, M.dark, 0, 0, .1);
  box(d, .014, .018, .1, M.orange, -.12, 0, .1); box(d, .014, .018, .1, M.orange, .12, 0, .1);
  return d;
}
/** Large pressure door (hangars, garages): split leaves, glowing frame. */
function pylon(p, c, x, z, h = .26, glow = M.cyan) {
  cyl(p, .012, .022, h, M.dark, x, Y, z, 8);
  cyl(p, .022, .022, .025, glow, x, Y + h * .72, z, 8);
  cyl(p, .026, .018, .02, M.white, x, Y + h, z, 8);
  sphere(p, .012, M.amber, x, Y + h + .025, z, 6);
}
const lamp = (p, x, y, z, h = .22) => pylon(p, { dyn: [] }, x, z, h);
/** Pressurized hab module: faceted pressure hull, ribs, glowing window band. */
function lander(p, c, x, y, z, s = 1, ry = 0) {
  const q = grp(p, x, y, z, ry); q.scale.setScalar(s);
  for (let k = 0; k < 4; k++) {
    const a = PI / 4 + k * PI / 2;
    pipe(q, [Math.sin(a) * .13, .2, Math.cos(a) * .13], [Math.sin(a) * .32, .015, Math.cos(a) * .32], .013, M.dark, 6);
    pipe(q, [Math.sin(a) * .17, .19, Math.cos(a) * .17], [Math.sin(a) * .3, .03, Math.cos(a) * .3], .008, M.steel, 5);
    cyl(q, .045, .05, .015, M.dark, Math.sin(a) * .32, 0, Math.cos(a) * .32, 10);
  }
  cyl(q, .07, .11, .1, M.darker, 0, .06, 0, 14);
  torus(q, .075, .01, M.lava, 0, .065, 0);
  const f = fspec(8, .2, .17, .2, .15);
  prism(q, 8, f.rb, f.rt, f.h, M.white, 0, .15, 0);
  prism(q, 8, .205, .205, .03, M.orange, 0, .2, 0);
  for (const i of [0, 3, 5]) { const w = faceGroup(q, f, i, .7, .002); boxc(w, .05, .03, .01, M.cyan); }
  add(q, new THREE.CylinderGeometry(.07, .17, .13, 8, 1, false, -PI / 8), M.panel, 0, .415, 0);
  cyl(q, .05, .06, .03, M.dark, 0, .48, 0, 8);
  mast(q, c, .04, .5, 0, .12, .006, M.amber);
  return q;
}
function powerCell(p, c, x, z, y0 = Y, r = .085, h = .34) {
  const q = grp(p, x, y0, z);
  cyl(q, r * 1.2, r * 1.3, .04, M.dark, 0, 0, 0, 12);
  cyl(q, r, r, h, M.white, 0, .04, 0, 12);
  for (const v of [.3, .55, .8]) cyl(q, r * 1.04, r * 1.04, .022, M.cyan, 0, .04 + h * v, 0, 12);
  add(q, new THREE.SphereGeometry(r, 12, 6, 0, TAU, 0, PI / 2), M.panel, 0, .04 + h, 0).scale.y = .5;
  cyl(q, r * .3, r * .3, .03, M.orange, 0, .04 + h + r * .45, 0, 8);
  return q;
}
function spire(p, c, x, z, y0, h, rb = .12) {
  const q = grp(p, x, y0, z);
  for (let k = 0; k < 3; k++) { const a = k * TAU / 3; box(q, .03, h * .2, rb * 1.7, M.white, Math.sin(a) * rb * .95, 0, Math.cos(a) * rb * .95, a); box(q, .032, .02, rb * 1.72, M.orange, Math.sin(a) * rb * .95, h * .2 - .02, Math.cos(a) * rb * .95, a); }
  let y = 0;
  [[rb, rb * .78, h * .4], [rb * .72, rb * .52, h * .33], [rb * .48, rb * .3, h * .27]].forEach(([b, t, hh]) => {
    cyl(q, t, b, hh, M.white, 0, y, 0, 12);
    cyl(q, b * 1.03, b * 1.0, .03, M.orange, 0, y + .02, 0, 12);
    y += hh;
    cyl(q, t * 1.25, t * 1.25, .035, M.dark, 0, y - .035, 0, 12);
    torus(q, t * 1.3, .01, M.cyan, 0, y - .017, 0);
  });
  cyl(q, .008, .016, h * .22, M.darker, 0, y, 0, 6);
  beacon(q, c, 0, y + h * .22, 0);
  q.userData.top = y; return q;
}
/** Small landing craft. */
function bioreactor(p, x, z, y0, r, h, glow = M.green) {
  cyl(p, r * 1.15, r * 1.2, .05, M.dark, x, y0, z, 14);
  cyl(p, r, r, h, M.glassClear, x, y0 + .05, z, 14);
  cyl(p, r * .82, r * .82, h * .92, glow, x, y0 + .06, z, 12);
  cyl(p, r * 1.1, r * 1.1, .05, M.white, x, y0 + .05 + h, z, 14);
  cyl(p, r * .3, r * .3, .04, M.orange, x, y0 + .1 + h, z, 8);
}

// ======================================================================
//  HEX-NATIVE KIT — every occupied cell is filled by a hex-plan block
//  (circumradius ≈ .935 → ~87 % of the hex), details sit ON the walls,
//  and every pipe ends on a wall, a tank, a collar or another pipe.
// ======================================================================
const AP = Math.cos(PI / 6), R0 = .935;
const mark = m => { m.userData.plinth = true; return m; };

function plinth(p, cells, fp) {
  const set = new Set(fp.map(c => c[0] + ',' + c[1]));
  cells.forEach(c => {
    mark(prism(p, 6, .975, .955, .06, M.dark, c.x, 0, c.z));
    mark(prism(p, 6, .935, .91, .04, M.slab, c.x, .06, c.z));
    for (let k = 0; k < 6; k++) { // orange plate inlay
      const a = k * PI / 3, ap = .78;
      mark(box(p, .5, .008, .012, M.orange, c.x + Math.sin(a) * ap, .098, c.z + Math.cos(a) * ap, a));
    }
    HEX_DIRS.forEach(([dq, dr]) => {
      if (set.has((c.q + dq) + ',' + (c.r + dr))) return;
      const w = axialToWorld(dq, dr), L = Math.hypot(w.x, w.z), nx = w.x / L, nz = w.z / L;
      const ap = .962 * Math.cos(PI / 6) + .004, ry = Math.atan2(nx, nz);
      mark(box(p, .3, .018, .01, M.amber, c.x + nx * ap, .02, c.z + nz * ap, ry));
      mark(box(p, .07, .019, .012, M.orange, c.x + nx * ap + nz * .3, .02, c.z + nz * ap - nx * .3, ry));
      mark(box(p, .07, .019, .012, M.orange, c.x + nx * ap - nz * .3, .02, c.z + nz * ap + nx * .3, ry));
    });
  });
}

// Modular footprint expansion for the host game's larger hexes.
// Every armour pod is a separate fixed-dimension module. No imported mesh,
// door or corridor is scaled. Outer edge clearance stays inside radius-3 hex.
function hostHexModules(group, cells, fp, gridRadius, buildingId) {
  if (gridRadius <= 1.1) return;
  const occupied = new Set(fp.map(([q,r]) => q + ',' + r));
  const isFlat = buildingId === '10A' || buildingId === '5A';
  for (const cell of cells) {
    for (const [dq,dr] of HEX_DIRS) {
      const local = axialToWorld(dq, dr);
      const angle = Math.atan2(local.z, local.x);
      const ux = Math.cos(angle), uz = Math.sin(angle);
      const outer = !occupied.has((cell.q+dq) + ',' + (cell.r+dr));
      // The kit's core stays in the middle. Six matching perimeter modules
      // occupy the otherwise empty area of each canonical game hex.
      const x = cell.x + ux * 1.80, z = cell.z + uz * 1.80;
      const ry = -angle;
      const h = isFlat ? .16 : outer ? .72 : .32;
      box(group, .72, h, 1.42, M.grey, x, .10, z, ry);
      box(group, .74, .09, 1.45, M.white, x, .10+h, z, ry);
      box(group, .12, .08, 1.2, M.orange, x+ux*.03, .15+h, z+uz*.03, ry);
      // Fixed-size structural link between the inner kit and the new pod.
      box(group, 1.02, .10, .28, M.dark, cell.x+ux*1.20, .10, cell.z+uz*1.20, ry);
    }
  }
}

// ---------------------------------------------------------------- blocks
/** Hex-plan armoured block: dark skirt, dark lower band, orange stripe, white upper body, cap rim. */
function blk(g, cell, h, o = {}) {
  const r = o.r ?? R0, y0 = o.y ?? Y, x = cell.x, z = cell.z;
  const bh = o.base ?? Math.min(.11, h * .26), by = y0 + .07 + bh, bodyH = h - .07 - bh - .04, tp = o.taper ?? 0;
  const q = { g, x, z, r, h, y0, top: y0 + h, ap: r * AP, cell };
  prism(g, 6, r + .035, r + .02, .07, M.dark, x, y0, z);
  prism(g, 6, r, r, bh, o.lower ?? M.dark, x, y0 + .07, z);
  prism(g, 6, r + .008, r + .008, .026, o.stripe ?? M.orange, x, by - .026, z);
  q.f = fspec(6, r - .004, r - .004 - tp, bodyH, by);
  prism(g, 6, q.f.rb, q.f.rt, bodyH, o.mat ?? M.white, x, by, z);
  q.fAll = fspec(6, r, r, h, y0); q.f.ox = q.fAll.ox = x; q.f.oz = q.fAll.oz = z;
  const rt = q.f.rt;
  prism(g, 6, rt + .026, rt + .014, .04, M.dark, x, q.top - .04, z);
  if (o.roofInset !== false) {
    const rr = rt * .84; prism(g, 6, rr, rr, .016, o.roofMat ?? M.panel, x, q.top - .004, z);
    for (let k = 0; k < 6; k++) { const a = k * PI / 3, ap = rr * AP - .02; box(g, rr * .9, .006, .014, M.orange, x + Math.sin(a) * ap, q.top + .011, z + Math.cos(a) * ap, a); }
    for (let k = 0; k < 3; k++) box(g, .012, .005, rr * 1.7, M.dark, x, q.top + .011, z, k * PI / 3);
  }
  q.top += .012;
  for (let k = 0; k < 6; k++) {
    const a = PI / 6 + k * PI / 3, px = x + Math.sin(a) * (r + .004), pz = z + Math.cos(a) * (r + .004);
    box(g, .05, h - .08, .05, M.dark, px, y0 + .07, pz, a);
    box(g, .056, .026, .056, M.orange, px, y0 + h - .1, pz, a);
  }
  return q;
}
/** Plain handle for non-block cells (domes, pads …) so bridge() can attach to them. */
const H = (x, z, ap, top, g) => ({ x, z, ap, top, g });

function hatch(q, i, s = 1) {
  const d = faceGroup(q.g, q.fAll, i, 0, .004), y = .075;
  box(d, .36 * s, .36 * s, .02, M.darker, 0, y, 0);
  box(d, .15 * s, .31 * s, .026, M.steel, -.078 * s, y + .015, 0);
  box(d, .15 * s, .31 * s, .026, M.steel, .078 * s, y + .015, 0);
  box(d, .008, .31 * s, .03, M.darker, 0, y + .015, 0);
  box(d, .3 * s, .02, .03, M.amber, 0, y + .345 * s, .002);
  for (const sx of [-1, 1]) box(d, .016, .1 * s, .03, M.amber, sx * .2 * s, y + .1, 0);
  box(d, .36 * s, .018, .06, M.dark, 0, y - .012, .03);
  for (let k = 0; k < 5; k++) box(d, .04, .02, .03, k % 2 ? M.darker : M.yellow, (-.14 + k * .07) * s, y - .004, .05);
  box(d, .07, .028, .03, M.cyan, 0, y + .375 * s, .004);
}
function bay(q, i, w = .5, hgt = .3) {
  const d = faceGroup(q.g, q.fAll, i, 0, .004), y = .075;
  box(d, w + .1, hgt + .08, .02, M.darker, 0, y, 0);
  box(d, w / 2 - .01, hgt, .03, M.steel, -w / 4, y + .01, 0); box(d, w / 2 - .01, hgt, .03, M.steel, w / 4, y + .01, 0);
  for (let k = 1; k < 4; k++) box(d, w, .006, .034, M.dark, 0, y + .01 + hgt * k / 4, 0);
  box(d, w + .08, .018, .034, M.amber, 0, y + hgt + .035, .002);
  box(d, w + .1, .02, .07, M.dark, 0, y - .012, .035);
  for (let k = 0; k < 8; k++) box(d, .05, .022, .035, k % 2 ? M.darker : M.yellow, -w / 2 + .03 + k * (w - .06) / 7, y - .004, .055);
  for (const sx of [-1, 1]) box(d, .016, hgt * .6, .036, M.amber, sx * (w / 2 + .07), y + .08, 0);
}
function deco(q, spec, glow = M.amber) {
  const g = q.g;
  for (const k of Object.keys(spec)) {
    const i = +k, kind = spec[k];
    if (kind === 'door') { hatch(q, i); continue; }
    if (kind === 'bay') { bay(q, i); continue; }
    const m = faceGroup(g, q.f, i, .5, .004), fw = m.userData.fw, hh = q.f.h;
    for (const sx of [-1, 1]) boxc(m, .008, hh * .88, .012, M.panel, sx * fw * .47, 0, 0);
    if (kind === 'win') for (const sx of [-1, 1]) { boxc(m, fw * .3, .1, .012, M.darker, sx * fw * .21, .02, 0); boxc(m, fw * .26, .072, .016, glow, sx * fw * .21, .02, .004); }
    else if (kind === 'wins') for (let j = -2; j <= 2; j++) { boxc(m, .1, .14, .012, M.darker, j * .17, .03, 0); boxc(m, .075, .11, .016, glow, j * .17, .03, .004); }
    else if (kind === 'vent') { boxc(m, fw * .5, .22, .012, M.darker, 0, 0, 0); for (let j = 0; j < 5; j++) boxc(m, fw * .46, .014, .02, M.steel, 0, -.085 + j * .043, .004); }
    else if (kind === 'scr') { boxc(m, fw * .52, .2, .014, M.darker, 0, .03, 0); boxc(m, fw * .46, .15, .016, M.screen, 0, .03, .004); boxc(m, fw * .52, .012, .02, M.amber, 0, -.1, .004); }
    else if (kind === 'plate') { boxc(m, fw * .56, .2, .012, M.panel, 0, 0, 0); for (const sx of [-1, 1]) boxc(m, .04, .2, .016, M.orange, sx * fw * .3, 0, .002); boxc(m, fw * .3, .02, .016, M.dark, 0, .06, .002); }
    else if (kind === 'haz') for (let j = -4; j <= 4; j++) boxc(m, .06, .07, .014, Math.abs(j) % 2 ? M.darker : M.yellow, j * .08, 0, 0);
    else if (kind === 'cross') { boxc(m, .2, .07, .014, M.teal, 0, .02, .002); boxc(m, .07, .2, .014, M.teal, 0, .02, .002); }
  }
}
function roof(q, c, kinds) {
  const g = q.g, x = q.x, z = q.z, y = q.top;
  for (const k of kinds) {
    if (k === 'spikes') for (let i = 0; i < 6; i++) {
      const a = PI / 6 + i * PI / 3, sx = x + Math.sin(a) * .74, sz = z + Math.cos(a) * .74;
      cyl(g, .004, .01, .22, M.darker, sx, y, sz, 5); sphere(g, .011, M.amber, sx, y + .22, sz, 6);
    } else if (k === 'solar') { solarPanel(g, x - .24, z, y, .42, .62, .35, 0, .08); solarPanel(g, x + .24, z, y, .42, .62, .35, 0, .08); }
    else if (k === 'vents') for (const [dx, dz] of [[-.3, .2], [0, -.3], [.3, .2]]) {
      box(g, .2, .06, .16, M.dark, x + dx, y, z + dz); box(g, .21, .014, .17, M.orange, x + dx, y + .06, z + dz);
      for (let j = 0; j < 3; j++) box(g, .18, .01, .02, M.steel, x + dx, y + .02 + j * .015, z + dz + .081);
    } else if (k === 'fans') { fan(g, c, x - .25, y, z + .1, .16, 4); fan(g, c, x + .25, y, z - .1, .16, 5); }
    else if (k === 'ac') for (const dx of [-.25, .25]) {
      box(g, .36, .14, .5, M.panel, x + dx, y, z); box(g, .37, .02, .51, M.orange, x + dx, y + .14, z); fan(g, c, x + dx, y + .16, z, .13, 6);
    } else if (k === 'cross') plus(g, .5, M.teal, x, y + .004, z);
    else if (k === 'beacon') { mast(g, c, x, y, z, .35, .012); }
    else if (k === 'tanks') for (const dx of [-.25, .25]) { cyl(g, .16, .16, .26, M.white, x + dx, y, z, 14); cyl(g, .165, .165, .03, M.cyan, x + dx, y + .1, z, 14); sphere(g, .1, M.panel, x + dx, y + .26, z, 8).scale.y = .5; }
  }
}
function HAB(g, c, i, h, o = {}) {
  const q = blk(g, c.cells[i], h, o), outs = c.outs(i), spec = {};
  const fc = o.faces ?? ['win', 'plate', 'vent', 'win', 'scr', 'plate'];
  outs.forEach((f, k) => { spec[f] = fc[k % fc.length]; });
  if (o.door !== false && outs.length) spec[outs[0]] = 'door';
  if (o.bay && outs.length > 1) spec[outs[1]] = 'bay';
  Object.assign(spec, o.spec || {});
  deco(q, spec, o.glow ?? M.amber);
  roof(q, c, o.roof ?? ['spikes']);
  return q;
}

// ---------------------------------------------------------------- links
function wallPt(q, face, y, out = 0) { const a = TAU * face / 6; return [q.x + Math.sin(a) * (q.ap + out), y, q.z + Math.cos(a) * (q.ap + out)]; }
function faceTo(A, B) { const a = Math.atan2(B.x - A.x, B.z - A.z); return ((Math.round(a / (PI / 3)) % 6) + 6) % 6; }
/** Pressurised tube between facing walls of two neighbouring cells; both ends embedded in the walls. */
function bridge(g, A, B, y = Y + .2, r = .085, mat = M.panel) {
  const fa = faceTo(A, B), fb = (fa + 3) % 6;
  const p = wallPt(A, fa, y, -.03), q = wallPt(B, fb, y, -.03);
  pipe(g, p, q, r, mat, 10);
  for (const [P, f, Q] of [[A, fa], [B, fb]]) { pipe(g, wallPt(P, f, y, -.02), wallPt(P, f, y, .035), r * 1.45, M.dark, 10); pipe(g, wallPt(P, f, y, .035), wallPt(P, f, y, .05), r * 1.2, M.amber, 10); }
  const m = [(p[0] + q[0]) / 2, y, (p[2] + q[2]) / 2], d = [q[0] - p[0], 0, q[2] - p[2]], L = Math.hypot(d[0], d[2]) || 1;
  pipe(g, [m[0] - d[0] / L * .02, y, m[2] - d[2] / L * .02], [m[0] + d[0] / L * .02, y, m[2] + d[2] / L * .02], r * 1.25, M.orange, 10);
}
/** Pipe from a world point on a wall to the wall of a round tank-like object {x,z,r}. */
function hookup(g, P, T, y = P[1], r = .025, mat = M.steel) {
  const d = Math.atan2(P[0] - T.x, P[2] - T.z);
  const E = [T.x + Math.sin(d) * (T.r - .02), y, T.z + Math.cos(d) * (T.r - .02)];
  pipe(g, [P[0], y, P[2]], E, r, mat);
  const n = [Math.sin(d), Math.cos(d)];
  pipe(g, [E[0] + n[0] * .04, y, E[2] + n[1] * .04], [E[0] - n[0] * .01, y, E[2] - n[1] * .01], r * 1.5, M.dark);
}
function joinWalls(g, A, B, y, r = .02, mat = M.steel) { // A,B = {x,z,r}
  const d = Math.atan2(B.x - A.x, B.z - A.z), s = Math.sin(d), co = Math.cos(d);
  pipe(g, [A.x + s * (A.r - .02), y, A.z + co * (A.r - .02)], [B.x - s * (B.r - .02), y, B.z - co * (B.r - .02)], r, mat);
}
function rackBetween(g, A, B, y0, n = 3, r = .026) { // n parallel pipes from wall A to wall B (blocks)
  const fa = faceTo(A, B), fb = (fa + 3) % 6;
  const mats = [M.orange, M.steel, M.panel];
  for (let k = 0; k < n; k++) {
    const y = y0 + k * .075, off = (k - (n - 1) / 2) * .13, a = TAU * fa / 6, nx = Math.cos(a), nz = -Math.sin(a);
    const p = wallPt(A, fa, y, -.03), q = wallPt(B, fb, y, -.03);
    pipe(g, [p[0] + nx * off, y, p[2] + nz * off], [q[0] + nx * off, y, q[2] + nz * off], r, mats[k % 3]);
  }
}

// ---------------------------------------------------------------- cell archetypes
function deckCell(g, cell, col = M.dark) {
  prism(g, 6, .96, .95, .08, col, cell.x, Y, cell.z);
  prism(g, 6, .9, .9, .016, M.slab, cell.x, Y + .08, cell.z);
  for (let k = 0; k < 6; k++) { const a = k * PI / 3; box(g, .46, .01, .014, M.orange, cell.x + Math.sin(a) * .76, Y + .095, cell.z + Math.cos(a) * .76, a); }
}
function holedHex(g, x, y, z, r, hr, h, mat, hx = 0, hz = 0) {
  const s = new THREE.Shape();
  for (let k = 0; k < 6; k++) { const a = k * PI / 3; if (k) s.lineTo(Math.cos(a) * r, Math.sin(a) * r); else s.moveTo(r, 0); }
  const pts = []; for (let k = 0; k < 28; k++) { const a = -k * TAU / 28; pts.push(new THREE.Vector2(hx + Math.cos(a) * hr, -hz + Math.sin(a) * hr)); }
  s.holes.push(new THREE.Path(pts));
  const geo = new THREE.ExtrudeGeometry(s, { depth: h, bevelEnabled: false }); geo.rotateX(-PI / 2);
  return add(g, geo, mat, x, y, z);
}
function domeCell(g, c, i, o = {}) {
  const cell = c.cells[i], x = cell.x, z = cell.z, r = o.r ?? .86, wh = o.wall ?? .2, fl = o.flatten ?? .62;
  deckCell(g, cell);
  prism(g, 6, .93, .91, .05, M.dark, x, Y + .09, z);
  cyl(g, r, r + .02, wh, o.mat ?? M.white, x, Y + .14, z, 28);
  cyl(g, r + .008, r + .008, .03, o.glow ?? M.amber, x, Y + .14 + wh * .5, z, 28);
  cyl(g, r + .015, r + .015, .026, M.orange, x, Y + .14 + wh - .02, z, 28);
  for (let k = 0; k < 14; k++) { const a = k * TAU / 14; box(g, .02, wh, .02, M.panel, x + Math.sin(a) * (r + .01), Y + .14, z + Math.cos(a) * (r + .01), a); }
  dome(g, r, o.glass ? M.glassClear : M.white, x, Y + .14 + wh, z, 8, M.panel, fl);
  const topY = Y + .14 + wh + r * fl;
  if (o.plants) { plants(g, x - .2, z - .1, Y + .14, .55, .34, 5, 3, .055); plants(g, x + .25, z + .2, Y + .14, .4, .3, 3, 3, .06); sphere(g, .12, M.plant2, x - .3, Y + .3, z + .3, 10); }
  torus(g, r * .24, .012, M.cyan, x, topY - .03, z);
  cyl(g, r * .22, r * .26, .06, M.dark, x, topY - .05, z, 14);
  // airlock stub on an outward face
  const outs = c.outs(i);
  if (o.door !== false && outs.length) {
    const a = TAU * outs[0] / 6, px = x + Math.sin(a) * (r - .05), pz = z + Math.cos(a) * (r - .05), q = grp(g, px, Y + .09, pz, a);
    box(q, .34, .3, .14, M.panel, 0, 0, 0); box(q, .36, .03, .16, M.orange, 0, .27, 0);
    box(q, .24, .22, .02, M.darker, 0, .02, .075); box(q, .1, .2, .026, M.steel, -.055, .03, .076); box(q, .1, .2, .026, M.steel, .055, .03, .076);
    box(q, .2, .016, .03, M.amber, 0, .26, .08);
  }
  return H(x, z, r + .02, topY, g);
}
function padCell(g, c, i, o = {}) {
  const cell = c.cells[i], x = cell.x, z = cell.z;
  prism(g, 6, .965, .955, .06, M.dark, x, Y, z);
  prism(g, 6, .93, .93, .06, M.concrete, x, Y + .06, z);
  torus(g, .66, .02, M.orange, x, Y + .122, z); torus(g, .44, .01, M.yellow, x, Y + .122, z); torus(g, .22, .008, M.yellow, x, Y + .122, z);
  for (let k = 0; k < 6; k++) { const a = PI / 6 + k * PI / 3; box(g, .05, .02, .05, M.amber, x + Math.sin(a) * .84, Y + .12, z + Math.cos(a) * .84, a); box(g, .15, .012, .03, M.yellow, x + Math.sin(a) * .56, Y + .122, z + Math.cos(a) * .56, a); }
  for (let k = 0; k < 6; k++) { const a = k * PI / 3; box(g, .3, .012, .05, k % 2 ? M.darker : M.yellow, x + Math.sin(a) * .74, Y + .12, z + Math.cos(a) * .74, a + PI / 2).scale.set(.35, 1, 1); }
  return H(x, z, .84, Y + .12, g);
}
function tanksCell(g, c, i, n = 3, o = {}) {
  const cell = c.cells[i], x = cell.x, z = cell.z; deckCell(g, cell);
  const P = { 1: [[0, 0, .8]], 2: [[-.46, 0, .44], [.46, 0, .44]], 3: [[0, .45, .37], [-.42, -.24, .37], [.42, -.24, .37]], 4: [[-.43, -.43, .38], [.43, -.43, .38], [-.43, .43, .38], [.43, .43, .38]] }[n];
  const T = P.map(([dx, dz, r], k) => {
    const h = o.h ?? .5 + (k % 2) * .06, bands = o.bands ?? [[.3, M.blue], [.75, M.orange]];
    tank(g, r, h, x + dx, z + dz, Y + .09, { bands, ladderAng: k * 2, rail: true, domeH: .3, mat: o.mat });
    return { x: x + dx, z: z + dz, r, h };
  });
  for (let a = 0; a < T.length; a++) for (let b = a + 1; b < T.length; b++) if (Math.hypot(T[a].x - T[b].x, T[a].z - T[b].z) < T[a].r + T[b].r + .3) joinWalls(g, T[a], T[b], Y + .09 + .26, .02, M.steel);
  return { T, x, z, ap: .83, top: Y + .09 + .5, g };
}
function columnsCell(g, c, i, list, o = {}) {
  const cell = c.cells[i], x = cell.x, z = cell.z; deckCell(g, cell);
  const T = list.map(([dx, dz, r, h], k) => {
    const cx = x + dx, cz = z + dz, y = Y + .09;
    cyl(g, r * 1.12, r * 1.2, .06, M.dark, cx, y, cz, 14);
    cyl(g, r, r, h, o.mat ?? M.white, cx, y + .06, cz, 16);
    for (const v of [.2, .45, .7]) cyl(g, r * 1.05, r * 1.05, .03, k % 2 ? M.orange : M.cyan, cx, y + h * v, cz, 16);
    add(g, new THREE.SphereGeometry(r, 16, 6, 0, TAU, 0, PI / 2), o.mat ?? M.white, cx, y + .06 + h, cz).scale.y = .45;
    cyl(g, r * .22, r * .26, .05, M.dark, cx, y + .06 + h + r * .38, cz, 8);
    box(g, .035, h * .8, .012, M.steel, cx + Math.sin(k) * (r + .008), y + .06, cz + Math.cos(k) * (r + .008), k);
    return { x: cx, z: cz, r, h };
  });
  for (let a = 0; a < T.length; a++) for (let b = a + 1; b < T.length; b++) if (Math.hypot(T[a].x - T[b].x, T[a].z - T[b].z) < T[a].r + T[b].r + .45) { joinWalls(g, T[a], T[b], Y + .09 + Math.min(T[a].h, T[b].h) * .35, .022, M.orange); joinWalls(g, T[a], T[b], Y + .09 + Math.min(T[a].h, T[b].h) * .52, .016, M.steel); }
  return { T, x, z, ap: .83, top: Y + .09 + .6, g };
}
function pitBlock(g, cell, h, hr, o = {}) {
  const r = o.r ?? .9, y0 = Y, x = cell.x, z = cell.z, th = o.th ?? .22, bh = .14, hx = o.hx ?? 0, hz = o.hz ?? 0;
  prism(g, 6, r + .035, r + .02, .07, M.dark, x, y0, z);
  prism(g, 6, r, r, bh, M.dark, x, y0 + .07, z);
  prism(g, 6, r + .008, r + .008, .026, M.orange, x, y0 + .07 + bh - .026, z);
  prism(g, 6, r - .004, r - .004, h - .07 - bh - th, M.white, x, y0 + .07 + bh, z);
  holedHex(g, x, y0 + h - th, z, r - .004, hr, th, M.white, hx, hz);
  prism(g, 6, r + .02, r + .01, .03, M.dark, x, y0 + h - .05, z);
  torus(g, hr + .018, .016, M.orange, x + hx, y0 + h + .002, z + hz, true, TAU, 28);
  add(g, new THREE.CylinderGeometry(hr, hr, th + .01, 28, 1, true), M.inner, x + hx, y0 + h - th / 2, z + hz);
  const fy = y0 + h - th;
  cyl(g, hr * .98, hr * .98, .008, M.darker, x + hx, fy, z + hz, 28);
  cyl(g, hr * .96, hr * .96, .006, M.lava, x + hx, fy + th * .55, z + hz, 28);
  cyl(g, hr * .5, hr * .5, .006, M.flame, x + hx, fy + th * .55 + .004, z + hz, 20);
  const q = { g, x, z, r, h, y0, top: y0 + h, ap: r * AP, cell, hx, hz, hr, fy };
  q.f = fspec(6, r - .004, r - .004, h - .07 - bh - th, y0 + .07 + bh); q.fAll = fspec(6, r, r, h, y0); q.f.ox = q.fAll.ox = x; q.f.oz = q.fAll.oz = z;
  return q;
}
function greenCell(g, c, i, o = {}) {
  const cell = c.cells[i], x = cell.x, z = cell.z, r = .93, y = Y;
  prism(g, 6, r + .03, r + .02, .1, M.dark, x, y, z);
  prism(g, 6, r - .02, r - .02, .02, M.soil, x, y + .1, z);
  for (const [dz, len] of [[-.42, .62], [0, .82], [.42, .62]]) for (const t of [0, 1]) {
    const yy = y + .14 + t * .2;
    box(g, len * 2, .018, .22, M.panel, x, yy, z + dz);
    box(g, len * 2 - .04, .07, .2, M.plant, x, yy + .018, z + dz);
    for (let k = 0; k < 6; k++) sphere(g, .05, k % 2 ? M.plant2 : M.plant, x - len + .12 + k * (len * 2 - .24) / 5, yy + .1, z + dz, 6);
    if (t) box(g, len * 2 - .04, .012, .04, o.glow ?? M.magenta, x, yy - .02, z + dz);
  }
  for (let k = 0; k < 6; k++) {
    const a = PI / 6 + k * PI / 3, px = x + Math.sin(a) * (r - .02), pz = z + Math.cos(a) * (r - .02);
    box(g, .035, .5, .035, M.white, px, y + .1, pz, a);
    pipe(g, [px, y + .6, pz], [x + Math.sin(a) * .42, y + .78, z + Math.cos(a) * .42], .012, M.white, 5);
  }
  prism(g, 6, r - .02, r - .02, .5, M.glassClear, x, y + .1, z);
  prism(g, 6, r - .02, .46, .28, M.glassClear, x, y + .6, z);
  for (let k = 0; k < 6; k++) { const a = k * PI / 3; box(g, .52, .028, .03, M.white, x + Math.sin(a) * (r - .02) * AP, y + .6, z + Math.cos(a) * (r - .02) * AP, a); }
  prism(g, 6, .47, .47, .045, M.white, x, y + .88, z);
  for (let k = 0; k < 6; k += 2) { const a = k * PI / 3; box(g, .2, .02, .14, M.orange, x + Math.sin(a) * .22, y + .925, z + Math.cos(a) * .22, a); }
  const outs = c.outs(i);
  if (outs.length) {
    const a = TAU * outs[0] / 6, q = grp(g, x + Math.sin(a) * (r * AP + .02), y + .1, z + Math.cos(a) * (r * AP + .02), a);
    box(q, .36, .34, .14, M.panel, 0, 0, 0); box(q, .38, .03, .16, M.orange, 0, .31, 0); box(q, .22, .26, .02, M.darker, 0, .02, .075);
    box(q, .09, .24, .026, M.steel, -.05, .03, .076); box(q, .09, .24, .026, M.steel, .05, .03, .076);
  }
  return H(x, z, r * AP + .02, y + .9, g);
}
function solarCell(g, c, i) {
  const cell = c.cells[i], x = cell.x, z = cell.z; deckCell(g, cell);
  for (const [dz, len] of [[-.55, .55], [-.2, .72], [.15, .78], [.5, .62]]) {
    const n = Math.max(2, Math.round(len * 2 / .34));
    for (let k = 0; k < n; k++) solarPanel(g, x - len + (k + .5) * len * 2 / n, z + dz, Y + .09, len * 2 / n - .03, .3, .5, 0, .13);
  }
  return H(x, z, .83, Y + .3, g);
}
function yardCell(g, c, i, o = {}) {
  const cell = c.cells[i], x = cell.x, z = cell.z; deckCell(g, cell);
  for (let k = 0; k < 3; k++) { // bunker walls
    const a = k * PI * 2 / 3 + PI / 6;
    box(g, .6, .22, .06, M.concrete, x + Math.sin(a) * .55, Y + .09, z + Math.cos(a) * .55, a + PI / 2).rotation.y = a + PI;
    pile(g, x + Math.sin(a) * .3, z + Math.cos(a) * .3, Y + .09, .3, .24 + k * .03, [M.regolith, M.rock, M.ore][k], 6);
  }
  return H(x, z, .83, Y + .3, g);
}
function plazaCell(g, c, i) {
  const cell = c.cells[i], x = cell.x, z = cell.z; deckCell(g, cell, M.concrete);
  for (let k = 0; k < 6; k++) { const a = k * PI / 3, px = x + Math.sin(a) * .68, pz = z + Math.cos(a) * .68; box(g, .3, .1, .2, M.concrete, px, Y + .09, pz, a); box(g, .26, .04, .16, M.soil, px, Y + .19, pz, a); sphere(g, .08, k % 2 ? M.plant : M.plant2, px, Y + .27, pz, 8); }
  cyl(g, .26, .28, .1, M.white, x, Y + .09, z, 16); cyl(g, .22, .22, .02, M.water, x, Y + .19, z, 16); cyl(g, .03, .03, .14, M.steel, x, Y + .2, z, 8);
  for (let k = 0; k < 6; k++) { const a = PI / 6 + k * PI / 3; cyl(g, .014, .018, .46, M.dark, x + Math.sin(a) * .74, Y + .09, z + Math.cos(a) * .74, 6); }
  prism(g, 6, .9, .5, .09, M.orange, x, Y + .55, z); prism(g, 6, .86, .46, .02, M.white, x, Y + .64, z);
  return H(x, z, .83, Y + .6, g);
}
function labDomeCell(g, c, i) {
  const h = domeCell(g, c, i, { wall: .2, flatten: .8, glow: M.cyan });
  return h;
}

// ---------------------------------------------------------------- props (reference style)
function crate(p, x, y, z, s = .09, ry = 0, mat = M.panel, ore = false) {
  const q = grp(p, x, y, z, ry);
  box(q, s, s * .92, s, mat, 0, 0, 0); box(q, s * 1.03, s * .12, s * 1.03, M.dark, 0, 0, 0); box(q, s * 1.03, s * .1, s * 1.03, M.orange, 0, s * .82, 0);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(q, s * .12, s * .92, s * .12, M.orange, sx * s * .46, 0, sz * s * .46);
  if (ore) for (let k = 0; k < 3; k++) add(q, new THREE.IcosahedronGeometry(s * .16, 0), M.rock, (k - 1) * s * .24, s * .98, ((k * 3) % 2 - .5) * s * .3);
  return q;
}
function crates(p, x, z, y0, nx, nz, layers = 1, s = .09, ry = 0, ore = false) {
  const q = grp(p, x, y0, z, ry), mats = [M.panel, M.white, M.grey];
  for (let l = 0; l < layers; l++) for (let i = 0; i < nx; i++) for (let j = 0; j < nz; j++)
    crate(q, (i - (nx - 1) / 2) * s * 1.06, l * s * .95, (j - (nz - 1) / 2) * s * 1.06, s, 0, mats[(i + j + l) % 3], ore && l === layers - 1);
  return q;
}
const pallet = (p, x, z, y0, n = 2, s = .09) => crates(p, x, z, y0, n, 2, 1, s);
function droneRef(p, x, y, z, s = 1, ry = 0) {
  const q = grp(p, x, y, z, ry); q.scale.setScalar(s);
  cyl(q, .085, .095, .03, M.darker, 0, 0, 0, 14);
  cyl(q, .055, .07, .2, M.white, 0, .03, 0, 12);
  cyl(q, .058, .058, .022, M.orange, 0, .2, 0, 12);
  sphere(q, .028, M.cyan, 0, .15, .058, 8);
  box(q, .2, .008, .085, M.dark, -.14, .21, -.03, .25); box(q, .2, .008, .085, M.dark, .14, .21, -.03, -.25);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) { box(q, .1, .035, .07, M.dark, sx * .13, .09, sz * .075); box(q, .09, .014, .06, M.orange, sx * .13, .125, sz * .075); }
  box(q, .3, .02, .03, M.white, 0, .1, 0);
  return q;
}
const drone = droneRef;
function roverRef(p, x, y, z, ry = 0, s = 1) {
  const q = grp(p, x, y, z, ry); q.scale.setScalar(s);
  box(q, .24, .05, .46, M.dark, 0, .08, 0);
  box(q, .24, .1, .3, M.white, 0, .13, .06);
  box(q, .22, .02, .26, M.orange, 0, .225, .06);
  box(q, .18, .07, .16, M.white, 0, .23, -.03);
  boxc(q, .16, .05, .012, M.glass, 0, .26, .06).rotation.x = -.5;
  box(q, .2, .1, .14, M.grey, 0, .13, -.2); box(q, .2, .02, .14, M.orange, 0, .23, -.2);
  box(q, .2, .03, .06, M.dark, 0, .09, .27); boxc(q, .06, .02, .01, M.cyan, -.07, .13, .24); boxc(q, .06, .02, .01, M.cyan, .07, .13, .24);
  for (const sx of [-1, 1]) for (const zz of [-.15, .17]) {
    const w = add(q, new THREE.CylinderGeometry(.082, .082, .075, 14), M.rubber, sx * .15, .082, zz); w.rotation.z = PI / 2;
    const rim = add(q, new THREE.CylinderGeometry(.05, .05, .08, 10), M.steel, sx * .15, .082, zz); rim.rotation.z = PI / 2;
    box(q, .09, .015, .15, M.dark, sx * .15, .17, zz);
  }
  cyl(q, .004, .004, .12, M.dark, .08, .24, -.22, 4); sphere(q, .008, M.amber, .08, .36, -.22, 6);
  return q;
}
const rover = roverRef;
function stack(p, c, r, h, x, z, y0 = Y, o = {}) {
  const q = grp(p, x, y0, z);
  cyl(q, r * 1.3, r * 1.4, .05, M.dark, 0, 0, 0, 12);
  cyl(q, r, r * 1.08, h, o.mat ?? M.steel, 0, .05, 0, 12);
  for (const v of (o.bands ?? [.3, .7])) cyl(q, r * 1.09, r * 1.09, .035, M.orange, 0, h * v, 0, 12);
  cyl(q, r * 1.2, r * 1.1, .05, M.dark, 0, h, 0, 12);
  cyl(q, r * .8, r * .8, .006, (o.glow || o.flame) ? M.lava : M.darker, 0, h + .05, 0, 12);
  if (o.flame) flame(q, c, 0, h + .05, 0, r * 1.2);
  if (o.smoke || o.steam) smoke(q, c, 0, h + .05, 0, r * 1.1, M.smoke);
  return q;
}
function flame(p, c, x, y, z, s) {
  const f = markDyn(add(p, new THREE.ConeGeometry(s * .6, s * 2.4, 8), M.flame, x, y + s * 1.2, z));
  c.dyn.push(t => { const k = 1 + .18 * Math.sin(t * 13 + x * 7) + .1 * Math.sin(t * 29); f.scale.set(1, k, 1); });
}
function smoke(p, c, x, y, z, s, mat = M.smoke) {
  const g = markDyn(grp(p, x, y, z)), puffs = [];
  for (let i = 0; i < 3; i++) puffs.push(sphere(g, s, mat, 0, 0, 0, 8));
  c.dyn.push(t => puffs.forEach((pf, i) => { const k = ((t * .18 + i / 3) % 1); pf.position.set(k * s * 1.4, k * s * 4.5, k * s * .4); pf.scale.setScalar(.45 + k * 1.3); pf.visible = k < .9; }));
}
function relayTower(g, c, x, z, y0, s = 1) {
  const q = grp(g, x, y0, z); q.scale.setScalar(s);
  prism(q, 6, .46, .46, .1, M.dark, 0, 0, 0);
  prism(q, 6, .44, .42, .52, M.white, 0, .1, 0);
  prism(q, 6, .45, .45, .028, M.orange, 0, .1, 0);
  const f = fspec(6, .44, .42, .52, .1);
  for (let i = 0; i < 6; i += 2) { const a = faceGroup(q, f, i, .5, .003); boxc(a, .22, .4, .012, M.darker, 0, 0, 0); for (let k = 0; k < 3; k++) boxc(a, .16, .018, .016, M.orange, 0, -.1 + k * .1, .004); }
  for (let k = 0; k < 6; k++) {
    const a = k * PI / 3, px = Math.sin(a) * .56, pz = Math.cos(a) * .56;
    cyl(q, .1, .1, .6, M.white, px, .06, pz, 14); cyl(q, .105, .105, .035, M.orange, px, .1, pz, 14); cyl(q, .105, .105, .035, M.orange, px, .52, pz, 14);
    cyl(q, .085, .1, .05, M.dark, px, .66, pz, 14); box(q, .03, .4, .012, M.panel, px + Math.sin(a) * .1, .12, pz + Math.cos(a) * .1, a);
    pipe(q, [px, .5, pz], [Math.sin(a) * .34, .5, Math.cos(a) * .34], .014, M.dark, 5);
  }
  prism(q, 6, .32, .3, .36, M.white, 0, .62, 0); prism(q, 6, .325, .325, .028, M.orange, 0, .66, 0);
  const f2 = fspec(6, .32, .3, .36, .62);
  for (let i = 0; i < 6; i++) { const a = faceGroup(q, f2, i, .6, .003); boxc(a, .16, .045, .01, M.amber, 0, 0, 0); }
  prism(q, 6, .36, .34, .05, M.dark, 0, .98, 0);
  cyl(q, .07, .09, .3, M.darker, 0, 1.03, 0, 10); torus(q, .09, .012, M.amber, 0, 1.2, 0);
  for (let k = 0; k < 4; k++) {
    const a = k * PI / 2 + PI / 4, px = Math.sin(a) * .2, pz = Math.cos(a) * .2;
    pipe(q, [0, 1.12, 0], [px, 1.2, pz], .014, M.dark, 5); box(q, .07, .26, .07, M.white, px, 1.15, pz, a); box(q, .075, .04, .075, M.orange, px, 1.38, pz, a);
    cyl(q, .005, .005, .14, M.dark, px, 1.41, pz, 4);
  }
  cyl(q, .01, .014, .3, M.darker, 0, 1.3, 0, 5); beacon(q, c, 0, 1.62, 0, M.red, .02);
  return q;
}


// =====================================================================
//  BUILDINGS  (cell indices: n=2 → 0,1 along z · n=3 → triangle ·
//  n=5 → centre + 4 diagonals · n=7 → centre + ring)
// =====================================================================
const B = {};
const cc = (c, i) => c.cells[i];
const HUB = (x = 0, z = 0) => H(x, z, .72, Y + .4);

B['1A'] = (g, c) => commandTower(g, c, 1.14);

B['1B'] = (g, c) => {
  commandTower(g, c, 1.14); const T = HUB();
  const a = HAB(g, c, 1, .5, { roof: ['solar', 'spikes'] });
  const b = HAB(g, c, 2, .44, { roof: ['fans', 'spikes'], glow: M.cyan, faces: ['scr', 'plate', 'vent', 'scr', 'plate', 'vent'] });
  const pd = padCell(g, c, 3); lander(g, c, pd.x, Y + .12, pd.z, 1.7, .4);
  const d = HAB(g, c, 4, .3, { roof: [] }); dish(g, c, d.x, d.top, d.z, .56, { spin: .25, yaw: 2.4, tilt: .8, post: .2 });
  const e = HAB(g, c, 5, .24, { roof: [], door: false }); relayTower(g, c, e.x, e.z, e.top, .6);
  const f = HAB(g, c, 6, .42, { roof: ['ac'], glow: M.teal });
  for (const h of [a, b, d, e, f]) bridge(g, T, h, Y + .17);
  bridge(g, T, H(pd.x, pd.z, .84, Y + .12), Y + .09, .05);
};

B['1C'] = (g, c) => {
  const q = blk(g, cc(c, 0), .14, { base: .04, roofInset: false });
  relayTower(g, c, 0, 0, q.top, 1);
  for (let k = 0; k < 6; k++) {
    const a = PI / 6 + k * PI / 3, px = Math.sin(a) * .78, pz = Math.cos(a) * .78;
    if (k % 2) powerCell(g, c, px, pz, q.top, .075, .24);
    else { box(g, .2, .22, .16, M.white, px, q.top, pz, a); box(g, .205, .02, .165, M.orange, px, q.top + .17, pz, a); boxc(g, .12, .05, .01, M.cyan, px + Math.sin(a) * .083, q.top + .1, pz + Math.cos(a) * .083, a); }
    pipe(g, [px, q.top + .12, pz], [Math.sin(a) * .4, q.top + .12, Math.cos(a) * .4], .014, M.dark, 6);
  }
};

B['1D'] = (g, c) => {
  const q = HAB(g, c, 0, .36, { roof: [], faces: ['plate', 'vent', 'win', 'plate', 'vent', 'scr'] });
  dish(g, c, 0, q.top, 0, .82, { spin: .12, tilt: .78, post: .2 });
  powerCell(g, c, .55, -.38, q.top, .07, .2); powerCell(g, c, -.55, .38, q.top, .07, .2);
};

B['1E'] = (g, c) => {
  const a = HAB(g, c, 0, .3, { roof: [], faces: ['plate', 'vent', 'win'] });
  dish(g, c, a.x, a.top, a.z, .64, { spin: .09, tilt: .85, post: .24 });
  const b = HAB(g, c, 1, .3, { roof: [], faces: ['plate', 'vent', 'win'] });
  dish(g, c, b.x, b.top, b.z, .64, { spin: .11, yaw: 1.4, tilt: .8, post: .24 });
  const d = HAB(g, c, 2, .46, { roof: ['spikes'], glow: M.cyan });
  spire(g, c, d.x, d.z, d.top, 1.0, .12);
  bridge(g, a, b, Y + .17); bridge(g, a, d, Y + .17); bridge(g, b, d, Y + .17);
};

B['2A'] = (g, c) => {
  const a = HAB(g, c, 0, .5, { roof: ['solar', 'vents'], faces: ['win', 'win', 'plate', 'win', 'plate', 'win'] });
  const b = HAB(g, c, 1, .5, { roof: ['tanks', 'ac'], glow: M.cyan, faces: ['plate', 'win', 'vent', 'win', 'scr', 'plate'] });
  bridge(g, a, b, Y + .24);
};

B['2B'] = (g, c) => {
  const d = domeCell(g, c, 0, { glass: true, plants: true, wall: .2, flatten: .66 });
  for (const i of [1, 2, 3, 4]) { const h = HAB(g, c, i, .5, { roof: i % 2 ? ['solar', 'spikes'] : ['solar', 'vents'], faces: ['win', 'win', 'plate', 'win', 'plate'] }); bridge(g, d, h, Y + .2); }
};

B['2C'] = (g, c) => {
  const q = HAB(g, c, 0, .3, { roof: [], faces: ['win', 'plate', 'win', 'vent', 'win', 'plate'] });
  railing(g, .76, q.x, q.top, q.z, 24);
  const u = blk(g, cc(c, 0), .3, { r: .66, y: q.top, base: .06, roofInset: false });
  deco(u, { 0: 'win', 1: 'win', 2: 'win', 3: 'win', 4: 'win', 5: 'win' }, M.amber);
  const t = u.top;
  solarPanel(g, -.2, 0, t, .34, .5, .3, 0, .06); solarPanel(g, .2, 0, t, .34, .5, .3, 0, .06); mast(g, c, .0, t, .38, .3, .01, M.amber);
};

B['2D'] = (g, c) => {
  const d = domeCell(g, c, 0, { glass: true, plants: true, wall: .2, flatten: .66, glow: M.green });
  const h = HAB(g, c, 1, .4, { roof: [], faces: ['wins', 'plate', 'wins', 'plate', 'wins', 'plate'] });
  prism(g, 6, .72, .4, .22, M.glassClear, h.x, h.top, h.z); prism(g, 6, .42, .42, .03, M.orange, h.x, h.top + .22, h.z);
  const p = plazaCell(g, c, 2);
  bridge(g, d, h, Y + .2);
};

B['3A'] = (g, c) => {
  const a = HAB(g, c, 0, .5, { roof: ['cross', 'ac'], glow: M.teal, faces: ['win', 'cross', 'win', 'plate', 'win', 'plate'] });
  const b = HAB(g, c, 1, .4, { roof: ['vents', 'spikes'], glow: M.cyan });
  const pd = padCell(g, c, 2); lander(g, c, pd.x, Y + .12, pd.z, 1.6, .2); plus(g, .5, M.teal, pd.x + .0, Y + .125, pd.z + .0);
  bridge(g, a, b, Y + .2); bridge(g, a, H(pd.x, pd.z, .84, Y + .12), Y + .09, .05); bridge(g, b, H(pd.x, pd.z, .84, Y + .12), Y + .09, .05);
};

B['3B'] = (g, c) => {
  const m = HAB(g, c, 0, .78, { roof: ['cross'], glow: M.teal, door: false, faces: ['win', 'cross', 'win', 'plate', 'win', 'plate'] });
  roof(m, c, ['beacon']);
  const w1 = HAB(g, c, 1, .46, { roof: ['ac', 'spikes'], glow: M.cyan });
  const pd = padCell(g, c, 2); lander(g, c, pd.x, Y + .12, pd.z, 1.6, .5); plus(g, .5, M.teal, pd.x, Y + .125, pd.z);
  const em = HAB(g, c, 3, .36, { roof: ['vents'], bay: true, glow: M.red, spec: {} });
  const w2 = HAB(g, c, 4, .46, { roof: ['solar', 'spikes'], glow: M.cyan });
  for (const h of [w1, em, w2]) bridge(g, m, h, Y + .2);
  bridge(g, m, H(pd.x, pd.z, .84, Y + .12), Y + .09, .05);
};

B['3C'] = (g, c) => {
  const q = HAB(g, c, 0, .46, { roof: ['spikes'], glow: M.red, door: false, faces: ['plate', 'win', 'plate', 'vent', 'plate', 'win'] });
  const outs = c.outs(0); deco(q, { [outs[0]]: 'bay', [outs[2]]: 'bay', [outs[4]]: 'door' }, M.red);
  box(g, .5, .04, .1, M.dark, q.x, q.top, q.z); beacon(g, c, q.x - .15, q.top + .06, q.z, M.red, .028, .6); beacon(g, c, q.x + .15, q.top + .06, q.z, M.amber, .028, .6);
  spire(g, c, q.x - .35, q.z - .25, q.top, .8, .07);
};

B['3D'] = (g, c) => {
  const x = cc(c, 0).x, z = cc(c, 0).z;
  prism(g, 6, .96, .95, .08, M.dark, x, Y, z);
  const f = fspec(6, .935, .56, .34, Y + .08); f.ox = x; f.oz = z;
  prism(g, 6, f.rb, f.rt, f.h, M.concrete, x, f.y0, z);
  for (let i = 0; i < 6; i++) {
    const q = faceGroup(g, f, i, .5, .003);
    for (const sx of [-1, 1]) boxc(q, .06, .3, .012, M.orange, sx * .3, 0, 0);
    if (i > 0) for (let k = 0; k < 3; k++) boxc(q, .3, .018, .014, M.darker, 0, .06 - k * .05, 0);
  }
  const d = faceGroup(g, f, 0, .0, .003);
  box(d, .5, .3, .03, M.darker, 0, 0, 0); box(d, .22, .26, .04, M.steel, -.12, .01, 0); box(d, .22, .26, .04, M.steel, .12, .01, 0);
  for (let k = 0; k < 7; k++) box(d, .05, .024, .05, k % 2 ? M.darker : M.yellow, -.18 + k * .06, 0, .12);
  prism(g, 6, .58, .52, .06, M.dark, x, Y + .42, z);
  cyl(g, .16, .18, .06, M.panel, x + .12, Y + .48, z + .1, 12); cyl(g, .12, .12, .012, M.amber, x + .12, Y + .54, z + .1, 12);
  for (const [dx, dz] of [[-.25, -.1], [-.05, .2]]) { pipe(g, [x + dx, Y + .46, z + dz], [x + dx, Y + .66, z + dz], .03, M.dark); pipe(g, [x + dx, Y + .66, z + dz], [x + dx + .1, Y + .66, z + dz], .03, M.dark); sphere(g, .045, M.dark, x + dx + .1, Y + .66, z + dz, 8); sphere(g, .035, M.dark, x + dx, Y + .46, z + dz, 8); }
  mast(g, c, x - .3, Y + .46, z + .2, .5, .01, M.amber);
};

B['4A'] = (g, c) => {
  const cell = { x: cc(c, 0).x, z: cc(c, 0).z - .1 }, h = .4;
  const q = pitBlock(g, cell, h, .46, { r: .8 });
  deco(q, { 1: 'door', 2: 'plate', 4: 'vent', 5: 'win' });
  const hx = q.x, hz = q.z - .2, hy = q.top + .42;
  // rig on a gantry column
  const col = grp(g, hx + .46, q.top, q.z - .4);
  box(col, .2, .62, .2, M.dark, 0, 0, 0); box(col, .21, .04, .21, M.orange, 0, .5, 0); box(col, .1, .3, .02, M.amber, 0, .12, .105);
  pipe(g, [hx + .46, q.top + .54, q.z - .4], [hx + .08, hy + .05, hz - .02], .045, M.dark, 8);
  const head = grp(g, hx, hy, hz);
  prism(head, 6, .27, .24, .13, M.dark, 0, 0, 0); prism(head, 6, .28, .28, .025, M.orange, 0, .08, 0);
  for (let k = 0; k < 6; k++) { const a = k * PI / 3; boxc(head, .12, .035, .01, M.amber, Math.sin(a) * .23, .07, Math.cos(a) * .23).rotation.y = a; }
  const beams = [];
  for (let k = 0; k < 3; k++) {
    const dx = (k - 1) * .11; cyl(head, .055, .06, .07, M.dark, dx, -.07, 0, 10); cyl(head, .04, .04, .02, M.amber, dx, -.08, 0, 10);
    const b = markDyn(cyl(g, .008, .008, hy - .07 - q.fy, M.flame, hx + dx, q.fy, hz, 6)); beams.push(b);
  }
  c.dyn.push(t => beams.forEach((b, k) => { b.scale.x = b.scale.z = .7 + .5 * Math.sin(t * 9 + k * 2); }));
  stack(g, c, .07, .3, q.x + .5, q.z + .35, q.top, { steam: true, bands: [.4] });
  pipe(g, [q.x + .5, q.top + .12, q.z + .35], [hx + .46, q.top + .12, q.z - .4], .03, M.orange, 8);
  // front belt with crates
  const bz = cc(c, 0).z + .78;
  boxc(g, .96, .04, .12, M.darker, q.x, Y + .1, bz); for (const sz of [-1, 1]) boxc(g, .96, .03, .014, M.steel, q.x, Y + .125, bz + sz * .06);
  for (let k = 0; k < 4; k++) crate(g, q.x - .33 + k * .22, Y + .12, bz, .09, 0, M.grey, true);
  for (const sx of [-1, 1]) { box(g, .12, .1, .16, M.dark, q.x + sx * .45, Y + .06, bz); box(g, .12, .014, .16, M.yellow, q.x + sx * .45, Y + .16, bz); }
};

B['4B'] = (g, c) => {
  const q = HAB(g, c, 0, .2, { roof: [], door: true, faces: ['plate', 'vent', 'win'] }), x = q.x, z = q.z;
  cyl(g, .62, .68, .72, M.white, x, q.top, z, 24);
  for (let k = 0; k < 16; k++) { const a = k * TAU / 16; box(g, .016, .56, .1, M.panel, x + Math.sin(a) * .66, q.top + .08, z + Math.cos(a) * .66, a); }
  for (const v of [.2, .5]) cyl(g, .68 - v * .07, .68 - v * .07, .03, M.cyan, x, q.top + .72 * v, z, 24);
  cyl(g, .63, .63, .03, M.orange, x, q.top + .66, z, 24); cyl(g, .64, .6, .05, M.dark, x, q.top + .72, z, 24);
  fan(g, c, x, q.top + .77, z, .5, 3);
  for (const k of [1, 3, 5]) { const a = PI / 6 + k * PI / 3 * .0 + k * PI / 3, px = x + Math.sin(a) * .78, pz = z + Math.cos(a) * .78; box(g, .18, .16, .16, M.white, px, q.top, pz, a); box(g, .185, .02, .165, M.blue, px, q.top + .11, pz, a); pipe(g, [px, q.top + .1, pz], [x + Math.sin(a) * .6, q.top + .1, z + Math.cos(a) * .6], .022, M.blue); }
};

B['4C'] = (g, c) => {
  const t = tanksCell(g, c, 0, 3, { bands: [[.3, M.blue], [.75, M.blue]] });
  const b = HAB(g, c, 1, .5, { roof: ['vents'], glow: M.cyan, faces: ['plate', 'win', 'scr', 'win', 'vent', 'plate'] });
  stack(g, c, .06, .3, b.x + .5, b.z - .3, b.top, { steam: true, bands: [.5] });
  for (const [dx, y] of [[-.18, Y + .2], [.18, Y + .32]]) hookup(g, wallPt(b, 3, y, .0).map((v, i) => i === 0 ? v + dx : v), t.T[0], y, .026, M.blue);
};

B['4D'] = (g, c) => {
  const q = HAB(g, c, 0, .34, { roof: [], glow: M.cyan, faces: ['plate', 'vent', 'win', 'scr', 'vent', 'plate'] }), x = q.x, z = q.z, y = q.top;
  const st = stack(g, c, .1, .85, x, z, y, { steam: true, bands: [.3, .6] });
  const cyls = [0, 1, 2].map(k => { const a = k * TAU / 3 + .3, px = x + Math.sin(a) * .5, pz = z + Math.cos(a) * .5; bioreactor(g, px, pz, y, .2, .5, M.cyan); return [px, pz, a]; });
  for (const [px, pz, a] of cyls) { const ty = y + .4; pipe(g, [px - Math.sin(a) * .0, y + .62, pz], [x, y + .62, z], .026, M.steel); pipe(g, [px, y + .55, pz], [px, y + .62, pz], .026, M.steel); }
};

B['4E'] = (g, c) => {
  const a = greenCell(g, c, 0), b = greenCell(g, c, 1), d = greenCell(g, c, 2);
  bridge(g, a, b, Y + .3, .08, M.glassClear); bridge(g, a, d, Y + .3, .08, M.glassClear); bridge(g, b, d, Y + .3, .08, M.glassClear);
  const o = cc(c, 2); for (const dz of [-.12, .12]) tank(g, .1, .3, o.x + .68, o.z + dz * 1.4, Y + .1, { bands: [[.5, M.cyan]], ladder: false });
};

B['4F'] = (g, c) => {
  const a = greenCell(g, c, 0, { glow: M.magenta }), b = greenCell(g, c, 1, { glow: M.magenta });
  bridge(g, a, b, Y + .3, .08, M.glassClear);
};

B['4G'] = (g, c) => {
  const m = HAB(g, c, 0, .6, { roof: [], glow: M.green, faces: ['plate', 'win', 'plate', 'win'] });
  tank(g, .24, .4, m.x - .28, m.z, m.top, { bands: [[.5, M.plant2]], ladder: false }); tank(g, .2, .32, m.x + .3, m.z + .14, m.top, { bands: [[.5, M.orange]], ladder: false });
  for (const i of [1, 2, 3, 4]) { const h = greenCell(g, c, i); bridge(g, m, h, Y + .3, .08, M.glassClear); }
};

B['4H'] = (g, c) => {
  const a = HAB(g, c, 0, .55, { roof: ['vents'], glow: M.amber, faces: ['plate', 'win', 'wins', 'vent', 'win', 'plate'] });
  stack(g, c, .06, .4, a.x - .4, a.z - .3, a.top, { steam: true });
  const t = tanksCell(g, c, 1, 3, { h: .6, bands: [[.4, M.orange]], mat: M.white });
  const P = wallPt(a, 0, Y + .3, 0);
  hookup(g, [P[0] - .15, P[1], P[2]], t.T[1], Y + .3, .03, M.orange); hookup(g, [P[0] + .15, P[1], P[2]], t.T[2], Y + .3, .03, M.steel);
  crates(g, a.x + .1, a.z - .62, a.top, 2, 1, 1, .09);
};

B['4I'] = (g, c) => {
  const hs = [0, 1, 2].map(i => {
    const q = HAB(g, c, i, .3, { roof: [], faces: ['plate', 'vent', 'win'] });
    cyl(g, .66, .7, .7, M.white, q.x, q.top, q.z, 24);
    for (let k = 0; k < 14; k++) { const a = k * TAU / 14; box(g, .014, .5, .08, M.dark, q.x + Math.sin(a) * .68, q.top + .08, q.z + Math.cos(a) * .68, a); }
    cyl(g, .7, .7, .03, M.cyan, q.x, q.top + .25, q.z, 24); cyl(g, .68, .68, .03, M.orange, q.x, q.top + .6, q.z, 24); cyl(g, .72, .66, .05, M.dark, q.x, q.top + .7, q.z, 24);
    fan(g, c, q.x, q.top + .75, q.z, .52, 2.5 + i); return q;
  });
  bridge(g, hs[0], hs[1], Y + .2, .1); bridge(g, hs[0], hs[2], Y + .2, .1); bridge(g, hs[1], hs[2], Y + .2, .1);
};

B['4J'] = (g, c) => {
  const a = HAB(g, c, 0, .6, { roof: [], faces: ['plate', 'wins', 'vent', 'scr', 'plate', 'win'] });
  railing(g, .62, a.x, a.top + .02, a.z, 20);
  for (const [dx, dz] of [[-.32, -.1], [0, -.2], [.32, -.1]]) stack(g, c, .12, .55, a.x + dx, a.z + dz, a.top, { steam: true, bands: [.3, .7] });
  stack(g, c, .06, .8, a.x - .45, a.z + .3, a.top, { flame: true, bands: [.3, .6] }); stack(g, c, .07, .65, a.x + .45, a.z + .3, a.top, { flame: true, bands: [.4] });
  boxc(g, .26, .12, .012, M.green, a.x, a.top - .3, a.z + .81).position.y = Y + .38;
  const b = cc(c, 1), bx = b.x, bz = b.z; deckCell(g, b);
  box(g, 1.0, .26, .14, M.panel, bx, Y + .09, bz - .72); box(g, 1.01, .02, .15, M.orange, bx, Y + .35, bz - .72);
  hTank(g, .17, .7, bx + .3, Y + .1, bz + .35, PI / 2, { band: M.orange }); hTank(g, .17, .7, bx + .3, Y + .1, bz + .05, PI / 2, { band: M.orange });
  box(g, .8, .08, .34, M.white, bx - .3, Y + .09, bz + .1); [M.green, M.yellow, M.rock].forEach((m, i) => { box(g, .22, .05, .3, m, bx - .53 + i * .23, Y + .17, bz + .1); box(g, .012, .1, .32, M.dark, bx - .415 + i * .23, Y + .09, bz + .1); });
  [M.green, M.orange, M.darker].forEach((m, i) => box(g, .12, .12, .12, m, bx - .5 + i * .16, Y + .09, bz + .52));
  crates(g, bx + .35, bz - .42, Y + .09, 2, 1, 1, .08);
  rackBetween(g, a, H(bx, bz, .8, Y + .3), Y + .12, 3, .024);
};

B['5A'] = (g, c) => {
  solarCell(g, c, 0); const x = cc(c, 0).x, z = cc(c, 0).z;
  box(g, .26, .3, .2, M.white, x + .5, Y + .09, z - .42, -.5); box(g, .27, .03, .21, M.orange, x + .5, Y + .38, z - .42, -.5); boxc(g, .16, .08, .01, M.screen, x + .44, Y + .27, z - .32, -.5);
  powerCell(g, c, x - .66, z - .42, Y + .09, .07, .26);
};

B['5B'] = (g, c) => {
  const d = domeCell(g, c, 0, { wall: .26, flatten: .8, glow: M.teal });
  radCell(g, c, 1, 3, d, 2);
};
function radCell(g, c, i, face, other, nPipes = 2) { // radiator array with header on the `face` side
  const cell = cc(c, i), a = TAU * face / 6, q = grp(g, cell.x, Y, cell.z, a);
  deckCell(g, cell);
  box(q, 1.0, .16, .14, M.panel, 0, .09, .72); box(q, 1.01, .02, .15, M.orange, 0, .25, .72);
  for (let k = 0; k < 5; k++) {
    const zz = .5 - k * .27, w = Math.min(1.0, 2 * (1 - Math.abs(zz) * .577 - .12));
    box(q, w, .42, .03, M.radiator, 0, .09, zz); box(q, w + .01, .02, .036, M.amber, 0, .51, zz);
    for (let j = 1; j < 5; j++) box(q, .008, .42, .034, M.panel, -w / 2 + j * w / 5, .09, zz);
    pipe(q, [0, .14, .72], [0, .14, zz], .016, M.orange, 6);
  }
  const hnd = { x: cell.x, z: cell.z, ap: .8, top: Y + .3, g };
  if (other) for (let k = 0; k < nPipes; k++) {
    const ox = (k - (nPipes - 1) / 2) * .36, y = Y + .2, f0 = faceTo(other, hnd), pa = wallPt(other, f0, y, -.02);
    const nx = Math.cos(TAU * f0 / 6), nz = -Math.sin(TAU * f0 / 6);
    const ph = [cell.x + Math.sin(a) * .66 + nx * ox, y, cell.z + Math.cos(a) * .66 + nz * ox];
    pipe(g, [pa[0] + nx * ox, y, pa[2] + nz * ox], ph, .03, k % 2 ? M.steel : M.orange);
    pipe(g, [pa[0] + nx * ox, y, pa[2] + nz * ox], wallPt(other, f0, y, .03).map((v, j) => j === 1 ? v : v + (j === 0 ? nx : nz) * ox), .045, M.dark);
  }
  return hnd;
}

B['5C'] = (g, c) => {
  const d = domeCell(g, c, 0, { wall: .3, flatten: .85, glow: M.teal, r: .88 });
  torus(g, .4, .025, M.teal, d.x, d.top - .02, d.z);
  radCell(g, c, 1, 3, d, 2); radCell(g, c, 2, 3, d, 2);
  const t = HAB(g, c, 3, .5, { roof: ['tanks', 'vents'], glow: M.cyan }), k = HAB(g, c, 4, .46, { roof: ['spikes', 'solar'], glow: M.cyan, faces: ['scr', 'plate', 'scr'] });
  bridge(g, d, t, Y + .2, .09); bridge(g, d, k, Y + .2, .09);
};

B['5D'] = (g, c) => {
  const x = cc(c, 0).x, z = cc(c, 0).z; deckCell(g, cc(c, 0));
  const P = [[0, 0]]; for (let k = 0; k < 6; k++) P.push([Math.sin(PI / 6 + k * PI / 3) * .56 * 1.0, Math.cos(PI / 6 + k * PI / 3) * .56 * 1.0]);
  const T = P.map(([dx, dz], k) => {
    const r = .31, h = .36 + (k % 3) * .08, cx = x + dx, cz = z + dz;
    prism(g, 6, r, r, h, M.white, cx, Y + .09, cz); prism(g, 6, r + .01, r + .01, .03, M.cyan, cx, Y + .09 + h * .4, cz); prism(g, 6, r + .01, r + .01, .03, M.orange, cx, Y + .09 + h - .04, cz);
    prism(g, 6, r * .6, r * .6, .02, M.dark, cx, Y + .09 + h, cz); fan(g, c, cx, Y + .11 + h, cz, .1, 5 + k);
    for (let j = 0; j < 3; j++) box(g, .2, .014, .012, M.green, cx, Y + .15 + j * .07, cz + r * AP + .004);
    return { x: cx, z: cz, r: r * AP + .02, h };
  });
  for (let k = 1; k <= 6; k++) joinWalls(g, T[0], T[k], Y + .3, .02, M.cyan);
  for (let k = 1; k <= 6; k++) { const n = k % 6 + 1; joinWalls(g, T[k], T[n], Y + .22, .016, M.orange); }
};

B['5E'] = (g, c) => {
  const q = HAB(g, c, 0, .26, { roof: [], faces: ['plate', 'vent', 'scr'] }), x = q.x, z = q.z, y = q.top;
  lattice(g, x, z, y, 1.15, .22, .06, 4, 6, M.steel, .012);
  for (const [h, w] of [[.7, .56], [.95, .44]]) { box(g, w, .025, .035, M.steel, x, y + h, z); for (const sx of [-1, 1]) cyl(g, .02, .02, .1, M.cyan, x + sx * (w / 2 - .02), y + h - .1, z, 8); }
  for (let k = 0; k < 3; k++) {
    const a = k * TAU / 3 + .5, px = x + Math.sin(a) * .5, pz = z + Math.cos(a) * .5;
    box(g, .3, .22, .24, M.dark, px, y, pz, a); box(g, .31, .025, .25, M.orange, px, y + .22, pz, a);
    for (let j = -1; j <= 1; j++) cyl(g, .02, .026, .12, M.white, px + Math.cos(a) * j * .08, y + .24, pz - Math.sin(a) * j * .08, 6);
    pipe(g, [px, y + .1, pz], [x + Math.sign(Math.sin(a)) * .146, y + .1, z + Math.sign(Math.cos(a)) * .146], .02, M.cyan, 6);
  }
};

B['6A'] = (g, c) => {
  const cell = cc(c, 0), x = cell.x, z = cell.z, px = x - .12, pz = z + .16, R = .7, hr = .5, ph = .18;
  deckCell(g, cell);
  add(g, new THREE.LatheGeometry([[hr, 0], [hr, ph], [R, ph], [R, 0]].map(a => new THREE.Vector2(a[0], a[1])), 36), M.white, px, Y, pz);
  torus(g, hr + .03, .02, M.orange, px, Y + ph + .004, pz, true, TAU, 36); torus(g, R - .04, .012, M.dark, px, Y + ph + .004, pz, true, TAU, 36);
  add(g, new THREE.CylinderGeometry(hr, hr, ph, 32, 1, true), M.inner, px, Y + ph / 2, pz);
  cyl(g, hr * .98, hr * .98, .006, M.darker, px, Y + .002, pz, 32); cyl(g, hr * .72, hr * .72, .006, M.lava, px, Y + .008, pz, 24); cyl(g, hr * .36, hr * .36, .006, M.flame, px, Y + .014, pz, 16);
  // rig
  const rx = x + .02, rz = z - .46, rg = grp(g, rx, Y + .09, rz);
  box(rg, .56, .46, .4, M.white, 0, 0, 0); box(rg, .57, .05, .41, M.dark, 0, 0, 0); box(rg, .57, .03, .41, M.orange, 0, .3, 0);
  box(rg, .3, .02, .22, M.darker, 0, .46, 0); box(rg, .24, .02, .16, M.panel, 0, .465, 0);
  for (const sx of [-1, 1]) { box(rg, .05, .46, .05, M.dark, sx * .29, 0, .2); box(rg, .06, .03, .06, M.orange, sx * .29, .4, .2); }
  boxc(rg, .26, .1, .01, M.amber, 0, .15, .205);
  for (const dx of [-.12, .12]) { pipe(g, [rx + dx, Y + .15, rz + .14], [px + dx * .8, Y + .02, pz - .18], .02, M.steel, 6); }
  // conveyor + ore pad
  const ox = x + .58, oz = z + .12;
  prism(g, 6, .34, .32, .12, M.dark, ox, Y, oz); prism(g, 6, .3, .3, .02, M.grey, ox, Y + .12, oz);
  pile(g, ox - .04, oz + .02, Y + .14, .2, .16, M.rock, 6); crate(g, ox + .15, Y + .14, oz - .12, .09, .3, M.grey, true); crate(g, ox + .02, Y + .14, oz + .2, .09, -.3, M.grey, true);
  conveyor(g, [rx + .3, Y + .45, rz + .08], [ox - .1, Y + .22, oz - .12], .08);
  box(g, .16, .2, .14, M.panel, x - .62, Y + .09, z - .3); box(g, .17, .02, .15, M.orange, x - .62, Y + .29, z - .3);
  stack(g, c, .04, .26, x - .5, z - .55, Y + .09, { steam: true, bands: [.5] });
  beacon(g, c, rx, Y + .6, rz, M.amber, .02, 1);
};

B['6B'] = (g, c) => {
  const m = HAB(g, c, 0, .6, { roof: [], door: false, faces: ['plate', 'win', 'plate', 'vent', 'plate', 'win'] });
  boxc(g, .38, .2, .02, M.darker, m.x, Y + .3, m.z + .815); boxc(g, .3, .13, .026, M.lava, m.x, Y + .3, m.z + .818);
  for (let k = 0; k < 3; k++) {
    const a = k * TAU / 3 + PI, vx = m.x + Math.sin(a) * .36, vz = m.z + Math.cos(a) * .36;
    cyl(g, .22, .26, .3, M.dark, vx, m.top, vz, 12); cyl(g, .24, .24, .03, M.orange, vx, m.top + .1, vz, 12); cyl(g, .2, .2, .012, M.lava, vx, m.top + .3, vz, 12);
    stack(g, c, .06, .32, vx, vz, m.top + .3, { steam: true, bands: [.5] });
  }
  const r = HAB(g, c, 1, .44, { roof: ['solar'], glow: M.amber });
  // robot arm on r
  const arm = grp(g, r.x - .1, r.top, r.z); cyl(arm, .1, .12, .06, M.dark, 0, 0, 0, 10);
  const s1 = markDyn(grp(arm, 0, .06, 0)); c.dyn.push(t => { s1.rotation.y = Math.sin(t * .8) * 1.2; });
  const j1 = grp(s1, 0, 0, 0); j1.rotation.x = .5; box(j1, .05, .36, .05, M.orange); const j2 = grp(j1, 0, .36, 0); j2.rotation.x = 1.4; box(j2, .045, .3, .045, M.white); box(j2, .08, .04, .08, M.dark, 0, .3, 0);
  const p = HAB(g, c, 2, .44, { roof: ['ac', 'vents'], glow: M.amber });
  rackBetween(g, m, p, Y + .1, 3, .026); bridge(g, m, r, Y + .2, .08);
  const y3 = yardCell(g, c, 3); const w = wallPt(m, 4, Y + .2, 0);
  conveyor(g, [y3.x + .15, Y + .3, y3.z - .1], [w[0] + .02, Y + .24, w[2] + .02], .1);
  const out = cc(c, 4), ox = out.x, oz = out.z; deckCell(g, out);
  for (let k = 0; k < 3; k++) for (let j = 0; j < 2; j++) box(g, .5, .1 + (k % 2) * .06, .22, M.steel, ox - .1 + (j - .5) * .0, Y + .09 + 0, oz - .5 + k * .3 + j * 0, 0).position.x = ox + (j ? .3 : -.3);
  crates(g, ox, oz + .55, Y + .09, 3, 1, 2, .09);
  const wz = wallPt(m, 5, Y + .2, 0); conveyor(g, [ox + .5, Y + .16, oz - .1], [wz[0] - .02, Y + .24, wz[2] + .0], .1, false);
};

B['6C'] = (g, c) => {
  const a = HAB(g, c, 0, .38, { roof: [], faces: ['plate', 'vent', 'win'] });
  add(g, new THREE.CylinderGeometry(.46, .16, .38, 6, 1, false, -PI / 6), M.white, a.x, a.top + .19, a.z); cyl(g, .47, .47, .03, M.orange, a.x, a.top + .36, a.z, 6, -PI / 6);
  pile(g, a.x, a.z, a.top + .36, .34, .12, M.regolith, 6);
  const b = HAB(g, c, 1, .7, { roof: ['vents'], glow: M.amber, faces: ['plate', 'win', 'vent', 'plate', 'win'] });
  boxc(g, .4, .2, .02, M.darker, b.x, Y + .4, b.z + .815);
  conveyor(g, [a.x, a.top + .55, a.z + .1], [b.x, b.top - .05, b.z - .5], .09);
  const k = cc(c, 2), kx = k.x, kz = k.z; deckCell(g, k);
  for (const sz of [-.4, .4]) { box(g, .3, .22, .14, M.dark, kx - .05, Y + .09, kz + sz * 1.0 * .0 + sz); }
  const kiln = markDyn(grp(g, kx - .05, Y + .45, kz)); kiln.rotation.x = PI / 2 + .05;
  cyl(kiln, .27, .27, 1.3, M.white, 0, -.65, 0, 18); for (const v of [-.9, -.65, -.4]) cyl(kiln, .285, .285, .05, M.orange, 0, v, 0, 18);
  c.dyn.push(t => { kiln.rotation.y = t * .6; });
  box(g, .34, .3, .28, M.dark, kx - .05, Y + .09, kz - .62); boxc(g, .2, .12, .01, M.lava, kx - .05, Y + .22, kz - .48);
  stack(g, c, .06, .5, kx + .45, kz - .4, Y + .09, { glow: true, steam: true });
  const wp = wallPt(b, 2, Y + .3, 0); conveyor(g, [wp[0], Y + .32, wp[2]], [kx - .05, Y + .65, kz + .62], .09);
  crates(g, kx + .45, kz + .45, Y + .09, 1, 2, 1, .09, 0, true);
  bridge(g, a, b, Y + .2);
};

B['7A'] = (g, c) => {
  const m = HAB(g, c, 0, .6, { roof: [], door: false, faces: ['plate', 'win', 'plate', 'vent'] });
  for (let k = 0; k < 3; k++) { const a = k * TAU / 3 + PI / 6; box(g, .5, .02, .22, M.glassClear, m.x + Math.sin(a) * .35, m.top, m.z + Math.cos(a) * .35, a); box(g, .52, .03, .24, M.orange, m.x + Math.sin(a) * .35, m.top + .02, m.z + Math.cos(a) * .35, a); }
  stack(g, c, .08, .55, m.x, m.z, m.top, { steam: true, bands: [.4] });
  const h1 = HAB(g, c, 1, .46, { roof: ['fans', 'vents'], bay: true }), h2 = HAB(g, c, 2, .46, { roof: ['fans', 'vents'], bay: true });
  const y3 = cc(c, 3), yx = y3.x, yz = y3.z; deckCell(g, y3);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) cyl(g, .035, .045, .6, M.orange, yx + sx * .56, Y + .09, yz + sz * .22, 8);
  for (const sz of [-1, 1]) box(g, 1.2, .05, .06, M.orange, yx, Y + .66, yz + sz * .22);
  box(g, .1, .06, .5, M.white, yx + .1, Y + .6, yz); box(g, .1, .07, .1, M.dark, yx + .1, Y + .52, yz + .1); pipe(g, [yx + .1, Y + .52, yz + .1], [yx + .1, Y + .3, yz + .1], .006, M.darker, 4); box(g, .05, .04, .05, M.dark, yx + .1, Y + .27, yz + .1);
  crates(g, yx - .3, yz - .08, Y + .09, 2, 2, 2, .09); box(g, .3, .12, .2, M.panel, yx + .3, Y + .09, yz + .25);
  const k = HAB(g, c, 4, .5, { roof: ['vents', 'tanks'], glow: M.amber });
  for (const h of [h1, h2]) bridge(g, m, h, Y + .2); bridge(g, m, k, Y + .2);
  const w = wallPt(m, 4, Y + .14, 0); conveyor(g, [yx + .55, Y + .14, yz + .1], [w[0] - .02, Y + .24, w[2] + .02], .1, false);
};

B['7B'] = (g, c) => {
  const a = HAB(g, c, 0, .62, { roof: ['vents', 'spikes'], door: false, glow: M.cyan, faces: ['plate', 'win', 'plate'] });
  const outs = c.outs(0); deco(a, { [outs[0]]: 'bay', [outs[2]]: 'door' });
  const b = cc(c, 1), bx = b.x, bz = b.z; deckCell(g, b);
  for (const sz of [-.5, .5]) { for (const sx of [-1, 1]) cyl(g, .03, .04, .62, M.orange, bx + sx * .45, Y + .09, bz + sz * .55, 8); box(g, 1.0, .05, .06, M.orange, bx, Y + .68, bz + sz * .55); }
  for (const sx of [-1, 1]) box(g, .05, .05, 1.1, M.yellow, bx + sx * .45, Y + .7, bz);
  box(g, .1, .05, 1.1, M.dark, bx, Y + .66, bz); roverRef(g, bx, Y + .09, bz, PI / 2, 1.5);
  pipe(g, [bx, Y + .66, bz + .1], [bx, Y + .34, bz + .1], .008, M.darker, 4); box(g, .06, .04, .06, M.dark, bx, Y + .31, bz + .1);
  const p = HAB(g, c, 2, .42, { roof: ['ac'], glow: M.amber }); crates(g, p.x, p.z - .6, p.top, 2, 1, 1, .09);
  bridge(g, a, p, Y + .2);
  const wb = wallPt(a, 0, Y + .3, 0); pipe(g, [wb[0] - .2, Y + .3, wb[2] - .02], [bx - .45, Y + .3, bz - .275], .03, M.orange);
};

B['7C'] = (g, c) => {
  const a = HAB(g, c, 0, .52, { roof: ['vents', 'solar'], door: false });
  const outs = c.outs(0); deco(a, { [outs[0]]: 'bay', [outs[1]]: 'door' });
  const b = cc(c, 1), bx = b.x, bz = b.z; deckCell(g, b);
  box(g, .6, .06, .8, M.dark, bx - .15, Y + .09, bz); box(g, .62, .012, .82, M.amber, bx - .15, Y + .15, bz); roverRef(g, bx - .15, Y + .16, bz, .1, 1.5);
  for (const sz of [-1, 1]) box(g, .06, .12, .7, M.yellow, bx - .15 + sz * .26, Y + .15, bz);
  const arm = grp(g, bx + .55, Y + .09, bz - .2); cyl(arm, .1, .12, .06, M.dark, 0, 0, 0, 12);
  const s1 = markDyn(grp(arm, 0, .06, 0)); c.dyn.push(t => { s1.rotation.y = -1.7 + Math.sin(t * .7) * .5; });
  const j1 = grp(s1, 0, 0, 0); j1.rotation.z = .5; box(j1, .05, .42, .05, M.orange); const j2 = grp(j1, 0, .42, 0); j2.rotation.z = 1.5; box(j2, .045, .34, .045, M.white); box(j2, .08, .05, .08, M.dark, 0, .34, 0); sphere(j2, .015, M.cyan, 0, .4, 0, 6);
  crates(g, bx + .6, bz + .4, Y + .09, 1, 2, 2, .09); powerCell(g, c, bx - .72, bz - .4, Y + .09, .07, .26);
  bridge(g, a, H(bx, bz, .8, Y + .15), Y + .13, .06);
};

B['8A'] = (g, c) => {
  const q = HAB(g, c, 0, .66, { roof: ['spikes', 'vents'], door: false, faces: ['plate', 'vent', 'plate', 'plate', 'vent', 'plate'], spec: { 0: 'bay' } });
  for (let i = 1; i < 6; i++) { const m = faceGroup(g, q.fAll, i, .12, .006); for (const sx of [-1, 1]) boxc(m, .05, .045, .012, i % 2 ? M.cyan : M.amber, sx * .3, 0, 0); }
};

B['8B'] = (g, c) => {
  const cell = cc(c, 0), x = cell.x, z = cell.z; deckCell(g, cell);
  lander(g, c, x, Y + .1, z, 2.1, .0);
  crates(g, x + .72, z, Y + .1, 1, 2, 1, .1, PI / 2); crates(g, x - .72, z, Y + .1, 1, 2, 1, .1, PI / 2, true);
  beacon(g, c, x + .05, Y + 1.15, z + .1, M.amber, .02, .7);
};

B['8C'] = (g, c) => {
  const a = yardCell(g, c, 0), b = cc(c, 1), bx = b.x, bz = b.z; deckCell(g, b);
  pile(g, bx - .22, bz - .08, Y + .09, .38, .26, M.regolith, 8); pile(g, bx + .26, bz + .18, Y + .09, .32, .22, M.ore, 8);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) cyl(g, .035, .045, .62, M.orange, bx + sx * .58, Y + .09, bz + sz * .3, 8);
  for (const sz of [-1, 1]) box(g, 1.3, .06, .07, M.orange, bx, Y + .7, bz + sz * .3);
  const tr = markDyn(grp(g, bx, Y + .7, bz)); c.dyn.push(t => { tr.position.x = bx + Math.sin(t * .5) * .35; });
  box(tr, .08, .07, .7, M.yellow, 0, 0, 0); box(tr, .1, .08, .1, M.dark, 0, -.07, 0); pipe(tr, [0, -.07, 0], [0, -.35, 0], .006, M.darker, 4); box(tr, .1, .05, .08, M.dark, 0, -.4, 0);
  const hp = wallPt(H(bx, bz, .8, 0), 3, Y + .3, 0);
  conveyor(g, [bx + .05, Y + .3, bz - .72], [a.x + .1, Y + .3, a.z + .72], .1);
};

B['8D'] = (g, c) => {
  const t = tanksCell(g, c, 0, 1, { h: .72, bands: [[.25, M.regolith], [.55, M.blue], [.85, M.orange]] }), x = t.x, z = t.z;
  for (const sx of [-1, 1]) { box(g, .15, .17, .12, M.white, x + sx * .8, Y + .09, z, 0); box(g, .16, .02, .13, M.blue, x + sx * .8, Y + .24, z); hookup(g, [x + sx * .8, Y + .16, z], t.T[0], Y + .16, .035, M.blue); }
};

B['8E'] = (g, c) => {
  const T = [0, 1, 2].map(i => tanksCell(g, c, i, 1, { h: .5, bands: [[.3, M.blue], [.62, M.blue], [.88, M.orange]] }));
  const [a, b, d] = T.map(t => t.T[0]);
  for (const [p, q] of [[a, b], [a, d], [b, d]]) { joinWalls(g, p, q, Y + .2, .04, M.blue); joinWalls(g, p, q, Y + .4, .03, M.steel); }
};

B['8F'] = (g, c) => {
  const cell = cc(c, 0), x = cell.x, z = cell.z; deckCell(g, cell);
  const r = .55, legH = .2, cy = Y + .09 + legH + r;
  sphereTank(g, r, x, z, Y + .09, legH, M.cyan);
  for (const sx of [-1, 1]) {
    hTank(g, .17, .6, x + sx * .55, Y + .09, z - .0, PI / 2, { band: M.cyan });
    const bx = x + sx * .55, by = Y + .09 + .2;
    pipe(g, [bx, by + .05, z - .02], [x + sx * (r - .02) * .96, cy - .15, z], .026, M.cyan);
  }
  railing(g, .4, x, Y + .09 + legH + r * 1.7, z, 12);
};

B['8G'] = (g, c) => {
  const q = HAB(g, c, 0, .54, { roof: ['ac'], mat: M.frost, stripe: M.cyan, glow: M.cyan, faces: ['plate', 'vent', 'plate', 'vent', 'plate', 'vent'] });
  const m = faceGroup(g, q.fAll, c.outs(0)[1], .4, .006); plus(m, .16, M.cyan, 0, 0, .004, false).rotation.z = PI / 4;
};

B['8H'] = (g, c) => {
  const a = HAB(g, c, 0, .5, { roof: ['spikes'], mat: M.cream, door: false, faces: ['plate', 'vent', 'plate', 'plate', 'vent', 'plate'], spec: { 3: 'bay' } });
  const b = HAB(g, c, 1, .5, { roof: ['spikes'], mat: M.cream, door: false, faces: ['plate', 'vent', 'plate', 'plate', 'vent', 'plate'], spec: { 0: 'bay' } });
  box(g, .9, .4, .13, M.cream, (a.x + b.x) / 2, Y + .09, (a.z + b.z) / 2); box(g, .91, .03, .14, M.orange, (a.x + b.x) / 2, Y + .4, (a.z + b.z) / 2);
  const d = cc(c, 2), dx = d.x, dz = d.z; deckCell(g, d);
  const arm = grp(g, dx + .55, Y + .09, dz); cyl(arm, .08, .1, .06, M.dark, 0, 0, 0, 10);
  const s1 = markDyn(grp(arm, 0, .06, 0)); c.dyn.push(t => { s1.rotation.y = Math.PI + Math.sin(t * 1.1) * 1.2; });
  const j1 = grp(s1, 0, 0, 0); j1.rotation.x = .4; box(j1, .045, .32, .045, M.orange); const j2 = grp(j1, 0, .32, 0); j2.rotation.x = 1.6; box(j2, .04, .26, .04, M.white); box(j2, .07, .04, .07, M.dark, 0, .26, 0);
  const pdx = dx - .1; prism(g, 6, .3, .3, .06, M.dark, pdx, Y + .09, dz - .4); torus(g, .2, .012, M.cyan, pdx, Y + .16, dz - .4); droneRef(g, pdx, Y + .16, dz - .4, 1.1);
  const dq = markDyn(grp(g, pdx, Y + .9, dz + .35)); droneRef(dq, 0, 0, 0, 1.2); c.dyn.push(t => { dq.position.y = Y + .8 + Math.sin(t * 1.3) * .06; dq.rotation.y = t * .5; });
  prism(g, 6, .3, .3, .06, M.dark, pdx, Y + .09, dz + .35); torus(g, .2, .012, M.cyan, pdx, Y + .16, dz + .35);
  crates(g, dx + .3, dz - .4, Y + .09, 2, 1, 2, .09);
  const w1 = wallPt(a, 1, Y + .14, 0), w2 = wallPt(b, 2, Y + .14, 0);
  conveyor(g, [w1[0] + .02, Y + .14, w1[2] - .02], [dx - .45, Y + .14, dz - .3], .1, false); conveyor(g, [w2[0] + .02, Y + .14, w2[2] + .02], [dx - .45, Y + .14, dz + .2], .1, false);
};

B['9A'] = (g, c) => {
  const slabs = [0, 1].map(i => {
    const cell = cc(c, i), x = cell.x, z = cell.z, ya = Y + .16;
    for (let k = 0; k < 6; k++) { const a = PI / 6 + k * PI / 3; box(g, .08, .1, .08, M.dark, x + Math.sin(a) * .86, Y + .0, z + Math.cos(a) * .86, a); cyl(g, .035, .05, .1, M.steel, x + Math.sin(a) * .86, Y + .06, z + Math.cos(a) * .86, 6); }
    prism(g, 6, .955, .945, .06, M.dark, x, ya - .02, z); prism(g, 6, .94, .94, .07, M.white, x, ya + .04, z); prism(g, 6, .945, .945, .02, M.yellow, x, ya + .02, z);
    for (const sx of [-1, 1]) {
      const bx = x + sx * .4; box(g, .6, .01, .78, M.darker, bx, ya + .11, z); box(g, .56, .004, .72, M.panel, bx, ya + .12, z);
      box(g, .5, .012, .02, M.cyan, bx, ya + .125, z + .3); box(g, .5, .012, .02, M.cyan, bx, ya + .125, z - .3); box(g, .02, .012, .6, M.cyan, bx - .24, ya + .125, z); box(g, .02, .012, .6, M.cyan, bx + .24, ya + .125, z);
      droneRef(g, bx, ya + .12, z, 1.4, sx * .3);
    }
    return H(x, z, .86, ya + .11, g);
  });
  const cell = cc(c, 0), cn = cc(c, 1), ya = Y + .16;
  box(g, .3, .12, .16, M.white, (cell.x + cn.x) / 2, ya, (cell.z + cn.z) / 2);
  const c0 = cc(c, 0); const scr = grp(g, c0.x, ya + .11, c0.z - .82); box(scr, .36, .16, .16, M.dark, 0, 0, 0); boxc(scr, .3, .1, .01, M.screen, 0, .09, .082).rotation.x = -.3; box(scr, .08, .04, .02, M.red, .15, .18, .0);
  for (const [i, sx] of [[0, -1], [1, 1]]) { const cell2 = cc(c, i); for (const s of [-1, 1]) { const px = cell2.x + s * .8, pz = cell2.z + (i ? .3 : -.3); cyl(g, .07, .08, .12, M.dark, px, ya + .11, pz, 10); torus(g, .07, .01, M.cyan, px, ya + .2, pz); cyl(g, .008, .008, .3, M.dark, px, ya + .2, pz, 4); sphere(g, .018, M.cyan, px, ya + .5, pz, 6); } }
  for (const i of [0, 1]) { const cl = cc(c, i), o = i ? 1 : -1; for (const sx of [-1, 1]) box(g, .5, .16, .04, M.white, cl.x + sx * .45, ya + .11, cl.z + o * .56, 0); }
};

B['9B'] = (g, c) => {
  [0, 1].forEach(i => {
    const cell = cc(c, i), x = cell.x, z = cell.z, o = c.outs(i)[0] ?? 0, a = TAU * o / 6, q = grp(g, x, Y, z, a);
    deckCell(g, cell);
    add(q, hullGeo(1.15, .66, 1.1, -.65, .46, 12), M.white, 0, .09, 0);
    box(q, .5, .014, 1.12, M.orange, 0, .09 + .66, -.1); const stripe = box(q, 1.0, .02, .3, M.orange, 0, .09 + .55, -.5);
    for (let k = 0; k < 4; k++) box(q, 1.17, .02, .04, M.dark, 0, .09, -.5 + k * .3);
    box(q, .8, .46, .02, M.darker, 0, .1, .455); box(q, .7, .3, .02, M.blue, 0, .12, .46); boxc(q, .74, .12, .02, M.cyan, 0, .6, .462);
    box(q, .86, .02, .03, M.amber, 0, .56, .47);
    for (const sx of [-1, 1]) { boxc(q, .02, .2, .6, M.glass, sx * .575, .45, -.1); box(q, .012, .2, .6, M.cyan, sx * .58, .35, -.1); }
    box(q, 1.0, .02, .3, M.dark, 0, .09, .74); for (let k = 0; k < 9; k++) box(q, .06, .022, .06, k % 2 ? M.darker : M.yellow, -.4 + k * .1, .1, .86);
    roverRef(q, 0, .09, .62, PI / 2, 1.1);
    solarPanel(q, -.3, -.2, .75, .4, .5, .15, 0, .04); cyl(q, .06, .07, .1, M.dark, .34, .75, -.3, 10); mast(q, c, .34, .85, -.3, .2, .008, M.cyan);
    crates(q, .42, .7, .09, 1, 1, 2, .09); crates(q, -.42, .7, .09, 1, 1, 1, .09, PI / 2);
  });
  bridge(g, H(cc(c, 0).x, cc(c, 0).z, .83, Y + .5), H(cc(c, 1).x, cc(c, 1).z, .83, Y + .5), Y + .3, .06);
};

B['9C'] = (g, c) => {
  const cell = cc(c, 0), x = cell.x, z = cell.z; deckCell(g, cell);
  for (let k = 0; k < 6; k++) { const a = PI / 6 + k * PI / 3; cyl(g, .04, .05, .5, M.white, x + Math.sin(a) * .84, Y + .09, z + Math.cos(a) * .84, 8); cyl(g, .045, .045, .03, M.orange, x + Math.sin(a) * .84, Y + .5, z + Math.cos(a) * .84, 8); }
  prism(g, 6, .93, .93, .05, M.white, x, Y + .59, z); prism(g, 6, .95, .95, .025, M.orange, x, Y + .58, z);
  for (let k = 0; k < 6; k++) { const a = k * PI / 3; box(g, .3, .012, .04, M.cyan, x + Math.sin(a) * .66, Y + .58, z + Math.cos(a) * .66, a); }
  solarPanel(g, x - .22, z, Y + .64, .4, .72, .12, 0, .03); solarPanel(g, x + .24, z, Y + .64, .4, .72, .12, 0, .03);
  const rv = roverRef(g, x, Y + .09, z + .1, PI / 2, 1.5);
  for (const sx of [-1, 1]) { const px = x + sx * .5, pz = z - .45; box(g, .14, .4, .12, M.white, px, Y + .09, pz); box(g, .145, .03, .125, M.orange, px, Y + .46, pz); boxc(g, .08, .1, .01, M.cyan, px, Y + .36, pz + .064); pipe(g, [px, Y + .3, pz + .03], [x + sx * .2, Y + .3, z + .1], .012, M.dark, 5); }
  hTank(g, .13, .6, x, Y + .09, z + .65, PI / 2, { band: M.orange });
};

B['10A'] = (g, c) => {
  const p = padCell(g, c, 0), x = p.x, z = p.z;
  lander(g, c, x, Y + .12, z, 2.0, .4);
  for (const a of [PI / 3, PI, -PI / 3]) { const fin = grp(g, x + Math.sin(a) * .8, Y + .12, z + Math.cos(a) * .8, a); box(fin, .34, .14, .04, M.concrete); box(fin, .3, .014, .02, M.amber, 0, .13, -.02); }
  const tw = grp(g, x + .72, Y + .12, z - .1); box(tw, .12, .7, .12, M.dark, 0, 0, 0); box(tw, .125, .04, .125, M.orange, 0, .56, 0);
  pipe(g, [x + .72, Y + .56, z - .1], [x + .2, Y + .56, z - .04], .02, M.cyan, 6); pipe(g, [x + .2, Y + .56, z - .04], [x + .15, Y + .5, z - .02], .028, M.dark, 6);
  beacon(g, c, x + .72, Y + .86, z - .1, M.amber, .022, 1.1);
};

B['10B'] = (g, c) => {
  const a = HAB(g, c, 0, .52, { roof: ['vents', 'spikes'], glow: M.cyan, door: false, faces: ['plate', 'wins', 'plate'] });
  const outs = c.outs(0); const gl = faceGroup(g, a.f, outs[0], .5, .004); boxc(gl, .7, .3, .02, M.glass, 0, .02, 0); for (let k = -3; k <= 3; k++) boxc(gl, .012, .3, .026, M.darker, k * .1, .02, 0); boxc(gl, .7, .012, .026, M.cyan, 0, -.1, 0); hatch(a, outs[1]);
  prism(g, 6, .62, .58, .2, M.glassClear, a.x, a.top, a.z); prism(g, 6, .64, .64, .03, M.orange, a.x, a.top + .2, a.z);
  const t = HAB(g, c, 1, .3, { roof: [], door: true });
  const tw = grp(g, t.x, t.top, t.z); cyl(tw, .2, .26, .75, M.white, 0, 0, 0, 12); cyl(tw, .27, .27, .03, M.orange, 0, .5, 0, 12); cyl(tw, .34, .26, .22, M.glass, 0, .75, 0, 12); torus(tw, .3, .012, M.cyan, 0, .86, 0); cyl(tw, .36, .36, .04, M.white, 0, .97, 0, 12); mast(tw, c, 0, 1.0, 0, .3, .012);
  const pd = padCell(g, c, 2), r = grp(g, pd.x, Y + .12, pd.z);
  for (let k = 0; k < 4; k++) { const ang = PI / 4 + k * PI / 2; pipe(r, [Math.sin(ang) * .3, 0, Math.cos(ang) * .3], [Math.sin(ang) * .13, .26, Math.cos(ang) * .13], .016, M.dark, 6); }
  cyl(r, .17, .17, 1.0, M.white, 0, .1, 0, 18); for (const v of [.3, .6, .85]) cyl(r, .175, .175, .035, M.orange, 0, v, 0, 18); add(r, new THREE.ConeGeometry(.17, .38, 18), M.white, 0, 1.29, 0); cyl(r, .1, .15, .1, M.darker, 0, 0, 0, 12);
  for (let k = 0; k < 4; k++) { const ang = k * PI / 2; box(r, .014, .24, .16, M.orange, Math.sin(ang) * .21, .12, Math.cos(ang) * .21, ang + PI / 2); }
  lattice(g, pd.x + .55, pd.z - .1, Y + .12, 1.15, .13, .09, 4, 7, M.orange, .012); pipe(g, [pd.x + .48, Y + .9, pd.z - .03], [pd.x + .14, Y + .9, pd.z - .02], .02, M.orange);
  bridge(g, a, t, Y + .2); bridge(g, a, H(pd.x, pd.z, .84, Y + .12), Y + .09, .05);
};

B['11A'] = (g, c) => {
  const a = HAB(g, c, 0, .52, { roof: ['solar', 'vents'], glow: M.cyan, faces: ['win', 'scr', 'plate', 'win', 'plate', 'scr'] });
  const o = domeCell(g, c, 1, { wall: .22, flatten: .8, glow: M.cyan }), ob = cc(c, 1);
  const sl = boxc(g, .16, .02, .7, M.darker, o.x, o.top - .12, o.z + .15); sl.rotation.x = -.55;
  const tel = grp(g, o.x, o.top - .15, o.z + .05); tel.rotation.x = -.8; cyl(tel, .07, .07, .34, M.dark, 0, 0, 0, 10);
  dish(g, c, a.x - .45, a.top, a.z - .2, .18, { tilt: .9, yaw: 2.5, post: .1 }); bridge(g, a, o, Y + .22);
};

B['11B'] = (g, c) => {
  const m = HAB(g, c, 0, .82, { roof: [], door: true, glow: M.cyan, faces: ['wins', 'plate', 'wins', 'plate', 'wins', 'plate'] });
  dish(g, c, m.x, m.top, m.z, .4, { spin: .3, tilt: .6, post: .12 });
  const o1 = domeCell(g, c, 1, { wall: .22, flatten: .8, glow: M.cyan }), o2 = domeCell(g, c, 2, { glass: true, plants: true, wall: .2, flatten: .66, glow: M.green });
  const acc = HAB(g, c, 3, .46, { roof: [], glow: M.cyan }); const ring = add(g, new THREE.TorusGeometry(.62, .07, 10, 40), M.white, acc.x, acc.top + .66, acc.z); ring.rotation.y = PI / 2 * 0; ring.rotation.x = PI / 2 * 0;
  const rg = add(g, new THREE.TorusGeometry(.62, .03, 6, 40), M.cyan, acc.x, acc.top + .66, acc.z);
  for (const sx of [-1, 1]) pipe(g, [acc.x + sx * .62, acc.top + .1, acc.z], [acc.x + sx * .62, acc.top + .66, acc.z], .05, M.dark, 8);
  const lab = HAB(g, c, 4, .46, { roof: ['tanks', 'ac'], glow: M.cyan });
  for (const h of [o1, o2, acc, lab]) bridge(g, m, h, Y + .22, .09);
};

B['12A'] = (g, c) => {
  const a = HAB(g, c, 0, .58, { roof: [], door: true, faces: ['plate', 'wins', 'vent', 'plate', 'win', 'vent'] });
  railing(g, .66, a.x, a.top + .02, a.z, 20);
  for (const [dx, dz] of [[-.3, -.2], [.05, -.25], [.34, -.12]]) stack(g, c, .12, .6, a.x + dx, a.z + dz, a.top, { steam: true, bands: [.3, .7], mat: M.white });
  stack(g, c, .05, .85, a.x - .4, a.z + .3, a.top, { flame: true, bands: [.3, .6, .85], mat: M.steel }); stack(g, c, .06, .7, a.x + .45, a.z + .28, a.top, { flame: true, bands: [.4], mat: M.steel });
  const b = tanksCell(g, c, 1, 3, { h: .5, bands: [[.3, M.orange], [.8, M.orange]], mat: M.white });
  const hc = cc(c, 2), hx = hc.x, hz = hc.z; deckCell(g, hc);
  const tk = [-.5, 0, .5].map(dz => { hTank(g, .2, .95, hx + .05, Y + .09, hz + dz * 1.2 * .0 + dz, PI / 2, { band: M.orange }); return dz; });
  const P1 = wallPt(a, 0, Y + .26, 0), P2 = wallPt(a, 1, Y + .36, 0);
  hookup(g, [P1[0] + .18, P1[1], P1[2]], b.T[1], Y + .26, .03, M.orange); hookup(g, [P1[0] - .18, P1[1], P1[2]], b.T[2], Y + .26, .03, M.steel);
  for (const [k, dz] of [[0, -.5], [1, 0], [2, .5]]) pipe(g, [P2[0], Y + .36 + 0, P2[2] + (k - 1) * .22], [hx - .35, Y + .1 + .2 + .09 - .09, hz + dz], .026, M.orange);
};

B['12B'] = (g, c) => {
  const cell = cc(c, 0), x = cell.x, z = cell.z; deckCell(g, cell);
  const sr = .68, sy = Y + .09 + .16 + sr; sphereTank(g, sr, x, z, Y + .09, .16, M.orange); railing(g, .5, x, sy + sr * .75, z, 14);
  [1, 2, 3, 4].forEach(i => {
    const cl = cc(c, i), ax = cl.x, az = cl.z; deckCell(g, cl);
    const dir = Math.atan2(ax - x, az - z);
    for (const dz of [-.46, 0, .46]) hTank(g, .19, 1.0, ax, Y + .09, az + dz, PI / 2, { band: i % 2 ? M.cyan : M.orange });
    box(g, .1, .1, 1.0, M.dark, ax + .4, Y + .09, az); for (const dz of [-.46, 0, .46]) pipe(g, [ax + .4, Y + .2, az + dz], [ax + .3, Y + .2, az + dz], .022, M.steel, 6);
    const P = [x + Math.sin(dir) * (sr - .02), Y + .09 + .16 + sr * .6, z + Math.cos(dir) * (sr - .02)];
    pipe(g, P, [ax + .4 - Math.sign(ax - x) * .0 + (Math.sign(ax - x) > 0 ? -.4 : .4) * 0, Y + .2, az], .03, M.steel);
  });
};

B['12C'] = (g, c) => {
  const a = HAB(g, c, 0, .52, { roof: ['ac'], mat: M.frost, stripe: M.cyan, glow: M.cyan, faces: ['plate', 'vent', 'plate', 'scr', 'plate', 'vent'] });
  const b = columnsCell(g, c, 1, [[-.28, -.1, .22, 1.2], [.3, .22, .18, .95], [.2, -.4, .14, .7]]);
  const d = HAB(g, c, 2, .36, { roof: ['fans', 'tanks'], glow: M.cyan });
  const P = wallPt(a, 0, Y + .3, 0);
  hookup(g, [P[0] - .15, P[1], P[2]], b.T[0], Y + .3, .03, M.cyan); hookup(g, [P[0] + .15, P[1], P[2]], b.T[2], Y + .4, .026, M.orange);
  bridge(g, a, d, Y + .2); const Q = wallPt(d, 2, Y + .14, 0); hookup(g, Q, b.T[1], Y + .14, .03, M.steel);
};

B['12D'] = (g, c) => {
  const T = [0, 1, 2, 3, 4].map(i => tanksCell(g, c, i, 1, { h: .8, bands: i ? [[.3, M.orange], [.7, M.orange]] : [[.25, M.cyan], [.5, M.cyan], [.85, M.cyan]] }).T[0]);
  for (let i = 1; i < 5; i++) { joinWalls(g, T[0], T[i], Y + .25, .035, M.orange); joinWalls(g, T[0], T[i], Y + .5, .028, M.cyan); }
};

// ---------------------------------------------------------------- registry
const CATS = ['Command & Communications', 'Habitation', 'Medical & Safety', 'Life Support & Food', 'Power', 'Mining & Materials', 'Manufacturing', 'Storage & Logistics', 'Drones & Vehicles', 'Spaceport', 'Science & Research', 'Gas & Propellant'];
const LIST = `1A Command Center 1|1B Command Complex 7|1C Relay Tower 1|1D Satellite Uplink Station 1|1E Long-Range Communications Array 3|2A Standard Habitat Module 2|2B Large Habitat Complex 5|2C Crew Quarters 1|2D Community Center 3|3A Medical Center 3|3B Medical Complex 5|3C Emergency Response Station 1|3D Emergency Bunker 1|4A Regolith Water Extractor 1|4B Atmospheric Water Harvester 1|4C Water Purification Plant 2|4D Oxygen Generator 1|4E Greenhouse 3|4F Hydroponics Facility 2|4G Large Agricultural Complex 5|4H Food Processing Facility 2|4I Atmospheric Processing Plant 3|4J Waste Recycling Plant 2|5A Solar Power Station 1|5B Power Reactor 2|5C Reactor Complex 5|5D Battery Storage Facility 1|5E Power Distribution Station 1|6A Mining Outpost 1|6B Ore Refinery 5|6C Regolith Processing Plant 3|7A Factory Complex 5|7B Vehicle Assembly Facility 3|7C Repair & Maintenance Workshop 2|8A General Storage Module 1|8B Remote Supply Module 1|8C Raw Material Storage Yard 2|8D Raw Water Storage Tank 1|8E Large Clean Water Reservoir 3|8F Oxygen Storage Tank 1|8G Cold Food Storage 1|8H Automated Logistics Hub 3|9A Drone Bay 2|9B Rover Garage 2|9C Charging & Refuelling Station 1|10A Landing Pad 1|10B Spaceport Terminal 3|11A Research Lab 2|11B Research Complex 5|12A Gas Refinery 3|12B Gas Storage Tank Plant 5|12C Propellant Refinery 3|12D Propellant Tank Farm 5`;

export const BUILDINGS = LIST.split('|').map(s => {
  const m = s.match(/^(\d+)([A-Z])\s+(.+)\s+(\d)$/);
  const cat = +m[1];
  return { id: m[1] + m[2], name: m[3], category: CATS[cat - 1], categoryIndex: cat, hexes: +m[4], footprint: FOOTPRINT_SHAPES[+m[4]] };
});
const BY_ID = Object.fromEntries(BUILDINGS.map(b => [b.id, b]));

/**
 * Build a building. Returns a THREE.Group whose origin is the anchor hex centre.
 * userData: { id, name, hexes, footprint, update(t) }
 */
export function createBuilding(id, footprintOverride = null, gridRadius = HEX_RADIUS) {
  getMaterials(); PIPES = [];
  const def = BY_ID[id]; if (!def) throw new Error('Unknown building ' + id);
  const fp = footprintOverride || def.footprint;
  const root = new THREE.Group(); root.name = `${def.id} ${def.name}`;
  const dyn = [];
  const ws = fp.map(([q, r]) => axialToWorld(q, r, gridRadius));
  const cx = ws.reduce((s, w) => s + w.x, 0) / ws.length, cz = ws.reduce((s, w) => s + w.z, 0) / ws.length;
  const body = grp(root, cx, 0, cz);
  const cells = ws.map((w, i) => ({ x: w.x - cx, z: w.z - cz, q: fp[i][0], r: fp[i][1] }));
  const has = new Set(fp.map(k => k[0] + ',' + k[1]));
  const nbr = (i, f) => { const [dq, dr] = HEX_DIRS[(f + 5) % 6]; return has.has((cells[i].q + dq) + ',' + (cells[i].r + dr)); };
  const outs = i => [0, 1, 5, 2, 4, 3].filter(f => !nbr(i, f));
  plinth(body, cells, fp);
  B[id](body, { cells, dyn, outs, nbr });
  hostHexModules(body, cells, fp, gridRadius, id);
  root.userData = { id: def.id, name: def.name, hexes: def.hexes, footprint: fp, dyn, pipes: PIPES, update: t => { for (const f of dyn) f(t); } };
  return root;
}
export function updateBuilding(root, t) { root.userData.update?.(t); }

/**
 * Merge all static meshes per material (big draw-call saving for a full colony).
 * Animated parts (dishes, fans, smoke, beacons…) are kept live.
 */
export function bakeBuilding(root) {
  root.updateMatrixWorld(true);
  const inv = root.matrixWorld.clone().invert(), groups = new Map(), victims = [];
  root.traverse(o => {
    if (!o.isMesh) return;
    for (let p = o; p && p !== root; p = p.parent) if (p.userData.dynamic) return;
    let geo = o.geometry.clone().applyMatrix4(inv.clone().multiply(o.matrixWorld));
    if (geo.index) geo = geo.toNonIndexed();
    for (const k of Object.keys(geo.attributes)) if (!['position', 'normal', 'uv'].includes(k)) geo.deleteAttribute(k);
    if (!geo.attributes.uv) geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(geo.attributes.position.count * 2), 2));
    geo.clearGroups();
    if (!groups.has(o.material)) groups.set(o.material, []);
    groups.get(o.material).push(geo); victims.push(o);
  });
  victims.forEach(o => { o.parent.remove(o); o.geometry.dispose(); });
  const baked = new THREE.Group(); baked.name = 'baked'; root.add(baked);
  for (const [mat, geos] of groups) {
    const m = new THREE.Mesh(mergeGeometries(geos, false), mat);
    m.castShadow = !mat.transparent; m.receiveShadow = true; baked.add(m);
  }
  return root;
}
